// src/screens/BusDash.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { apiFetch, getAuthToken, getOrgContext, setOrgContext, setAuthToken } from '../api/client';

import tealBack from '../Images/teal_dback.png';
import rypilLo from '../Images/rypilLo.png';
import syncLogo from '../Images/rypilsync_white.png'; // top-left logo
import tipEligibleIcon from '../Images/tipEligible.png';

// Ensure non-apiFetch navigations use the same API namespace
const API_PREFIX = (process.env.REACT_APP_API_PREFIX || '/api').replace(/\/+$/, '');

// Backward/forward-compatible wallet policy fetch
async function fetchWalletPolicy(apiFetch, brandId) {
  const qs = `?brandId=${encodeURIComponent(brandId)}`;
  try {
    // Old/primary path
    return await apiFetch(`/brand/wallet/policy${qs}`);
  } catch (err) {
    // Fallback if you moved it to /wallet/policy
    try {
      return await apiFetch(`/wallet/policy${qs}`);
    } catch {
      throw err;
    }
  }
}

const PEACH = '#EBA477';
// Google-ish fallback icon if we don't have a logo:
const DEFAULT_BRAND_ICON =
  'https://maps.gstatic.com/mapfiles/place_api/icons/v2/generic_pinlet.png';

/* ---------- Policy (frontend fallback) ---------- */
const FRONTEND_POLICY_MAP = {
  'Real Estate & Property':        { minTopUpCents: 75000, initialTopUpCents: 75000, reloadCents: 50000, autoThresholdCents: 25000, manualThresholdCents: 30000 },
  'Home Services & Trades':        { minTopUpCents: 50000, initialTopUpCents: 50000, reloadCents: 30000, autoThresholdCents: 20000, manualThresholdCents: 25000 },
  'Travel & Hospitality':          { minTopUpCents: 30000, initialTopUpCents: 30000, reloadCents: 15000, autoThresholdCents: 15000, manualThresholdCents: 17500 },
  'Education & Childcare':         { minTopUpCents: 30000, initialTopUpCents: 30000, reloadCents: 20000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  'Home & Garden':                 { minTopUpCents: 25000, initialTopUpCents: 25000, reloadCents: 15000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  'Office & Professional Services':{ minTopUpCents: 20000, initialTopUpCents: 20000, reloadCents: 10000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  Automotive:                      { minTopUpCents: 25000, initialTopUpCents: 25000, reloadCents: 15000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  'Electronics & Tech':            { minTopUpCents: 20000, initialTopUpCents: 20000, reloadCents: 10000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  Grocery:                         { minTopUpCents: 25000, initialTopUpCents: 25000, reloadCents: 15000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  'Sports & Outdoors':             { minTopUpCents: 25000, initialTopUpCents: 25000, reloadCents: 15000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  'Wellness & Fitness':            { minTopUpCents: 20000, initialTopUpCents: 20000, reloadCents: 10000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  'Health & Beauty':               { minTopUpCents: 20000, initialTopUpCents: 20000, reloadCents: 10000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  'Apparel & Accessories':         { minTopUpCents: 20000, initialTopUpCents: 20000, reloadCents: 10000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  Restaurants:                     { minTopUpCents: 25000, initialTopUpCents: 25000, reloadCents: 15000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  'Pet Supplies & Services':       { minTopUpCents: 17500, initialTopUpCents: 17500, reloadCents: 10000, autoThresholdCents: 7500, manualThresholdCents: 10000 },
  'Specialty Retail':              { minTopUpCents: 20000, initialTopUpCents: 20000, reloadCents: 10000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  'Arts & Culture':                { minTopUpCents: 15000, initialTopUpCents: 15000, reloadCents: 7500, autoThresholdCents: 7500, manualThresholdCents: 10000 },
  'Bars & Breweries':              { minTopUpCents: 15000, initialTopUpCents: 15000, reloadCents: 7500, autoThresholdCents: 7500, manualThresholdCents: 10000 },
  'Cafe & Bakery':                 { minTopUpCents: 15000, initialTopUpCents: 15000, reloadCents: 7500, autoThresholdCents: 7500, manualThresholdCents: 10000 },
  'Entertainment & Recreation':    { minTopUpCents: 20000, initialTopUpCents: 20000, reloadCents: 10000, autoThresholdCents: 10000, manualThresholdCents: 12500 },
  'Financial & Insurance Services':{ minTopUpCents: 15000, initialTopUpCents: 15000, reloadCents: 7500, autoThresholdCents: 7500, manualThresholdCents: 10000 },
};
function pickFallbackPolicy(category) {
  if (!category) return FRONTEND_POLICY_MAP['Specialty Retail'];
  const keys = Object.keys(FRONTEND_POLICY_MAP);
  const hit = keys.find(k => k.toLowerCase() === String(category).toLowerCase());
  return FRONTEND_POLICY_MAP[hit || 'Specialty Retail'];
}

const FALLBACK_EXCLUSION_REASONS = [
  { code: 'renovation', label: 'Under renovation' },
  { code: 'seasonal', label: 'Seasonal closure' },
  { code: 'ownership_change', label: 'Ownership change in progress' },
  { code: 'temporary_closure', label: 'Temporarily closed' },
  { code: 'staffing', label: 'Staffing constraints' },
];

/* ---------- NEW: account-level mix helpers ---------- */

// Robust "active" check for stores/locations
const isStoreActive = (s) => {
  if (!s) return false;
  const status = String(s.status || s.lifecycleStatus || '').toLowerCase();
  const flaggedInactive =
    s.isActive === false || s.disabled === true || s.tempExcluded === true;
  return !flaggedInactive && (status === 'active' || status === 'enrolled' || status === 'live');
};

// Summarize active store counts by category across all brand docs
function summarizeActiveByCategory(docsMap) {
  const counts = new Map(); // category -> activeCount
  Object.values(docsMap || {}).forEach((doc) => {
    const cat = doc?.category || 'Specialty Retail';
    const stores = Array.isArray(doc?.stores) ? doc.stores : [];
    const activeCount = stores.filter(isStoreActive).length;
    if (activeCount > 0) {
      counts.set(cat, (counts.get(cat) || 0) + activeCount);
    }
  });
  return counts;
}

// Apply per-category policy to the account mix
function computeTotalsFromMix(counts) {
  const totals = {
    minTopUpCents: 0,
    initialTopUpCents: 0,
    reloadCents: 0,
    autoThresholdCents: 0,
    manualThresholdCents: 0,
  };
  let storesActive = 0;

  counts.forEach((count, category) => {
    const p = pickFallbackPolicy(category);
    storesActive += count;
    totals.minTopUpCents        += count * p.minTopUpCents;
    totals.initialTopUpCents    = (totals.initialTopUpCents || 0) + count * (p.initialTopUpCents || p.minTopUpCents);
    totals.reloadCents          += count * p.reloadCents;
    totals.autoThresholdCents   += count * p.autoThresholdCents;
    totals.manualThresholdCents += count * p.manualThresholdCents;
  });

  return { storesActive, totals };
}

/* ---------- Tiny helpers ---------- */
const dollars = (cents) =>
  typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '$0.00';

const centsToDollar = (cents) =>
  typeof cents === 'number' && !Number.isNaN(cents)
    ? `$${(cents / 100).toFixed(2)}`
    : '—';

const fmtInt = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');

const titleCase = (s) => String(s || '').replace(/\b\w/g, (m) => m.toUpperCase());

const digits = (x) => String(x || '').replace(/\D/g, '');
const normalizeEin = (raw) => digits(raw).slice(0, 9) || '';
const formatEin = (raw) => {
  const d = normalizeEin(raw);
  return d.length === 9 ? `${d.slice(0, 2)}-${d.slice(2)}` : '';
};

function readAnyEin(obj) {
  if (!obj) return '';
  const candidates = [
    obj.einTaxId, obj.ein, obj.taxId, obj.einNumber, obj.employerId,
    obj.fein, obj.federalEin,
    obj?.tax?.ein, obj?.registration?.ein, obj?.registration?.einTaxId,
    obj.einLabel,
  ];
  for (const v of candidates) {
    const d = normalizeEin(v);
    if (d) return d;
  }
  return '';
}

function safeBrandLogo(brand) {
  return brand?.logo || brand?.assets?.logo?.url || '';
}

/* ---------- UI atoms ---------- */
const ButtonBase = ({ children, style, ...props }) => (
  <button
    {...props}
    style={{
      borderRadius: 12,
      padding: '10px 16px',
      cursor: props.disabled ? 'not-allowed' : 'pointer',
      fontWeight: 600,
      transition: 'background 120ms ease, color 120ms ease, border 120ms ease',
      ...style,
    }}
  >
    {children}
  </button>
);

const PeachButton = ({ children, style, ...props }) => (
  <ButtonBase
    {...props}
    style={{
      background: PEACH,
      color: '#1A1A1A',
      border: '1px solid rgba(0,0,0,0.05)',
      ...style,
    }}
  >
    {children}
  </ButtonBase>
);

const OutlineButton = ({ children, style, ...props }) => (
  <ButtonBase
    {...props}
    style={{
      background: 'transparent',
      color: 'white',
      border: '1px solid rgba(255,255,255,0.32)',
      ...style,
    }}
  >
    {children}
  </ButtonBase>
);

const GhostButton = ({ children, style, ...props }) => (
  <ButtonBase
    {...props}
    style={{
      background: 'transparent',
      color: 'rgba(255,255,255,0.86)',
      border: 'none',
      ...style,
    }}
  >
    {children}
  </ButtonBase>
);

const FrostedCard = ({ style, children, className }) => (
  <section
    className={className}
    style={{
      background: 'rgba(19,36,46,0.72)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 16,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      padding: 24,
      color: 'white',
      boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
      ...style,
    }}
  >
    {children}
  </section>
);

const StatCell = ({ label, value }) => (
  <div style={styles.statCell}>
    <div style={styles.statLabel}>{label}</div>
    <div style={styles.statValue}>{value ?? '—'}</div>
  </div>
);

const TipEligibleBadge = () => (
  <span style={styles.tipBadge}>
    <img src={tipEligibleIcon} alt="Tips allowed" style={styles.tipBadgeIcon} />
    Tip Adjustment Eligible
  </span>
);

const NewLocationBadge = ({ count }) => (
  <span style={styles.newLocationBadge}>
    {count && count > 1 ? `${count} new` : 'New location'}
  </span>
);

const BrandAvatar = ({ logoUrl, name }) => (
  <div style={styles.brandAvatarOuter}>
    {logoUrl ? (
      <img
        src={logoUrl}
        alt={name || 'Brand logo'}
        style={styles.brandAvatarImg}
        onError={(e) => {
          if (e.currentTarget.src !== DEFAULT_BRAND_ICON) {
            e.currentTarget.src = DEFAULT_BRAND_ICON;
          }
        }}
      />
    ) : (
      <div style={styles.brandAvatarFallback}>
        {(name || '?').slice(0, 1).toUpperCase()}
      </div>
    )}
  </div>
);

const HeaderBar = ({
  userName,
  lastLoginDisplay,
  onAddBusiness,
  onAddStore,
  canAddStore,
  addStoreHint,
  onLogout,
  canAddBusiness = true,
}) => (
  <div style={styles.headerRow}>
    <FrostedCard style={styles.headerCard}>
      <div style={styles.headerLeftGroup}>
        <div style={styles.headerLogoWrap}>
          <img src={syncLogo} alt="rypilSync" style={styles.headerLogo} />
        </div>
        <div style={styles.headerIdentity}>
          <div style={styles.headerUser}>{userName}</div>
          <div style={styles.headerContext}>
            <span style={styles.headerContextLabel}>Last login</span>
            <span style={styles.headerContextValue}>{lastLoginDisplay}</span>
          </div>
        </div>
      </div>
      <div style={styles.headerActions}>
        {canAddBusiness ? (
          <PeachButton onClick={onAddBusiness}>+ Add Business</PeachButton>
        ) : null}
        <div style={styles.headerStoreAction}>
          <OutlineButton onClick={onAddStore} disabled={!canAddStore}>
            + Add Store
          </OutlineButton>
          {!canAddStore && addStoreHint ? (
            <span style={styles.headerStoreHint}>{addStoreHint}</span>
          ) : null}
        </div>
        <GhostButton onClick={onLogout}>Logout</GhostButton>
      </div>
    </FrostedCard>
  </div>
);

const WalletAndPaymentsCard = ({ data, onSetMode, modeDisabled }) => (
  <FrostedCard className="WalletCard" style={styles.walletCard}>
    <div style={styles.cardHeaderRow}>
      <div style={styles.cardTitle}>Wallet &amp; Payments</div>
      <div style={styles.cardMeta}>as of {data.asOfDisplay || '—'}</div>
      <div style={styles.modeToggleWrap}>
        <button
          type="button"
          style={{
            ...styles.modeToggleBtn,
            ...(data.mode === 'auto' ? styles.modeToggleActive : {}),
          }}
          onClick={() => onSetMode?.('auto')}
          disabled={modeDisabled || data.mode === 'auto'}
        >
          Auto
        </button>
        <button
          type="button"
          style={{
            ...styles.modeToggleBtn,
            ...(data.mode === 'manual' ? styles.modeToggleActive : {}),
          }}
          onClick={() => onSetMode?.('manual')}
          disabled={modeDisabled || data.mode === 'manual'}
        >
          Manual
        </button>
      </div>
    </div>

    <div style={styles.walletGrid}>
      <StatCell label="Wallet Balance" value={data.walletBalance} />
      <StatCell label="Payment Type" value={data.paymentTypeDisplay} />
      <StatCell label="Last Top-Up" value={data.lastTopUp || '—'} />
      <StatCell label="Minimum Top-Up" value={data.minTopUp} />
      <StatCell label="Replenish Amount" value={data.replenishAmount} />
      <StatCell label="Top-Up Threshold" value={data.threshold} />
      <StatCell label="Funded to Date" value={data.fundedToDate} />
      <StatCell label="Spent on Contributions" value={data.spentOnContributions} />
      <StatCell label="Spent on Fees" value={data.spentOnFees} />
    </div>

    <div style={styles.walletActions}>
      <PeachButton onClick={data.onFundWallet}>Fund Wallet</PeachButton>
      <OutlineButton onClick={data.onViewWalletHistory}>View Wallet History</OutlineButton>
    </div>
  </FrostedCard>
);

const PerformanceCard = ({ data }) => (
  <FrostedCard style={styles.perfCard}>
    <div style={styles.cardHeaderRow}>
      <div style={styles.cardTitle}>Account Performance</div>
      <div style={styles.cardMeta}>as of {data.asOfDisplay || '—'}</div>
    </div>
    <div style={styles.perfGrid}>
      <StatCell label="Contributions (Lifetime)" value={data.contribLifetime} />
      <StatCell label="Contributions (Last 30d)" value={data.contrib30d} />
      <StatCell label="Fees (Lifetime)" value={data.feesLifetime} />
      <StatCell label="Fees (Last 30d)" value={data.fees30d} />
      <StatCell label="Transactions (Lifetime)" value={data.txLifetime} />
      <StatCell label="Transactions (Last 30d)" value={data.tx30d} />
      <StatCell label="Eligible Sales (Lifetime)" value={data.eligibleSalesLifetime} />
      <StatCell label="Eligible Sales (Last 30d)" value={data.eligibleSales30d} />
    </div>
  </FrostedCard>
);

/* ---------- Row for each public-facing brand card ---------- */
const BusinessAccordionRow = ({
  biz,
  attention = [],
  isOpen,
  onToggle,
  onResolve,
  onOpenContributionChange,
  contributionBrandId,
}) => {
  const needsAttention = Array.isArray(attention) && attention.length > 0;

  const handleResolve = (candidate, event) => {
    if (event) event.stopPropagation();
    if (typeof onResolve === 'function') onResolve(candidate);
  };

  return (
    <div
      className={`BizRow ${isOpen ? 'BizRow-open' : ''}`}
      style={{
        ...styles.bizRow,
        ...(isOpen ? styles.bizRowOpen : {}),
        ...(needsAttention ? styles.bizRowAttention : {}),
      }}
    >
      <div
        style={styles.bizRowHeader}
        onClick={onToggle}
        role="button"
        tabIndex={0}
      >
        <div style={styles.bizRowLeft}>
          <BrandAvatar logoUrl={biz.logoUrl} name={biz.displayName} />
          <div style={styles.bizRowText}>
            {/* Line 1: Brand Name + Tip eligibility */}
            <div style={styles.bizRowLine1}>
              <span style={styles.bizRowName}>{biz.displayName}</span>
              {biz.tipEligible && <TipEligibleBadge />}
              {needsAttention && <NewLocationBadge count={attention.length} />}
            </div>

            {/* Line 2: EINs */}
            <div style={styles.bizRowLine2}>
              {biz.einTaxIdDisplay
                ? `EIN${biz.einTaxIdDisplay.includes('•') ? 's' : ''} ${biz.einTaxIdDisplay}`
                : 'EIN Unassigned'}
            </div>

            {/* Line 3: Category • Lifecycle • Reg Date */}
            <div style={styles.bizRowLine3}>
              <span>{biz.category}</span>
              <span>• {biz.lifecycleStatusLabel}</span>
              <span>• Reg: {biz.regDateDisplay}</span>
            </div>

            {/* Line 4: coverage summary */}
            <div style={styles.bizRowLine4}>
              Coverage — {biz.coverageSummary}
            </div>
            <div style={styles.bizRowLine5}>
              Base Contribution • {biz.baseContributionDisplay || '—'}
              <button
                type="button"
                style={styles.inlineLinkButton}
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenContributionChange?.({
                    brandId: contributionBrandId,
                    displayName: biz.displayName,
                    currentRateDisplay: biz.baseContributionDisplay || '—',
                  });
                }}
                disabled={!contributionBrandId}
                title="Schedule a future base contribution rate change."
              >
                Change contribution rate
              </button>
            </div>
            <div style={styles.bizRowLine6}>
              Contributions · 30d {biz.contribLast30Display || '—'} • Lifetime{' '}
              {biz.contribLifetimeDisplay || '—'}
            </div>
          </div>
        </div>

        <div style={styles.bizRowRight}>
          {needsAttention ? (
            <span style={styles.attentionPill}>
              {attention.length} need review
            </span>
          ) : null}
          <span style={styles.chevron}>{isOpen ? '▾' : '▸'}</span>
        </div>
      </div>

      {isOpen && (
        <div style={styles.bizRowBody}>
          {needsAttention ? (
            <div style={styles.attentionPanel}>
              <div style={styles.attentionPanelHeader}>
                Locations needing attention
              </div>
              {attention.map((candidate) => (
                <div
                  key={
                    candidate._id ||
                    candidate.placeId ||
                    candidate.googlePlaceId ||
                    candidate.locationId ||
                    candidate.name ||
                    candidate.formattedAddress
                  }
                  style={styles.attentionRow}
                >
                  <div style={styles.attentionRowMain}>
                    <div style={styles.attentionRowName}>
                      {candidate.name || 'New location'}
                    </div>
                    <div style={styles.attentionRowMeta}>
                      {candidate.formattedAddress ||
                        candidate.address ||
                        'Address unavailable'}
                    </div>
                    <div style={styles.attentionRowMeta}>
                      Source: {(candidate.source || 'unknown').toUpperCase()}
                      {candidate.marketKey
                        ? ` • Market ${candidate.marketKey}`
                        : ''}
                    </div>
                  </div>
                  <div style={styles.attentionRowActions}>
                    <button
                      type="button"
                      style={styles.resolveButton}
                      onClick={(event) => handleResolve(candidate, event)}
                    >
                      Resolve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {biz.locations.length === 0 ? (
            <div style={{ opacity: 0.75 }}>No locations enrolled yet.</div>
          ) : (
            biz.locations.map((loc) => (
              <div key={loc.id} style={styles.locationRow}>
                <div style={styles.locationMain}>
                  <div style={styles.locationName}>{loc.address}</div>
                  <div
                    style={{
                      ...styles.locationStatus,
                      ...(styles[`status_${loc.statusCode}`] || {}),
                    }}
                  >
                    {loc.statusLabel}
                  </div>
                </div>
                {loc.statusCode === 'temp_excluded' && loc.exclusionReason ? (
                  <div style={styles.locationNote}>{loc.exclusionReason}</div>
                ) : null}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const BusinessSection = ({
  businesses,
  currentBrandId,
  attention,
  onToggleRow,
  isRowOpen,
  loading,
  onResolve,
  onOpenContributionChange,
}) => (
  <FrostedCard style={styles.businessSectionCard}>
    <div style={styles.cardHeaderRow}>
      <div style={styles.cardTitle}>Enrolled Businesses</div>
      <div style={styles.cardMeta}>
        {businesses.totalLocations} total • {businesses.activeLocations} enrolled
        locations
      </div>
    </div>

    <div style={styles.businessList}>
      {loading ? (
        <div style={styles.emptyState}>Loading enrolled businesses…</div>
      ) : businesses.items.length === 0 ? (
        <div style={styles.emptyState}>No enrolled businesses yet.</div>
      ) : (
        businesses.items.map((biz) => (
          (() => {
            const contributionBrandId =
              Array.isArray(biz.brandIds) && currentBrandId && biz.brandIds.includes(currentBrandId)
                ? currentBrandId
                : (Array.isArray(biz.brandIds) ? biz.brandIds[0] : null);
            return (
          <BusinessAccordionRow
            key={biz.id}
            biz={biz}
            attention={
              Array.isArray(biz.brandIds) &&
              biz.brandIds.includes(currentBrandId)
                ? attention
                : []
            }
            isOpen={isRowOpen(biz.id)}
            onToggle={() => onToggleRow(biz.id)}
            onResolve={onResolve}
            onOpenContributionChange={onOpenContributionChange}
            contributionBrandId={contributionBrandId}
          />
            );
          })()
        ))
      )}
    </div>
  </FrostedCard>
);

/* ---------- Banner grouping helpers ---------- */

/**
 * Return an array of "public-facing" names (banners) for a brand doc.
 * We intentionally do NOT use aliases/AKA here because aliases are search help,
 * not separate storefront banners.
 */
function getBannerNames(brand) {
  const clean = (s) => (typeof s === 'string' ? s.trim() : '');
  const possible = [
    clean(brand?.name),
    clean(brand?.dbaName),
    // forward compat if backend stores extra banner names:
    ...(Array.isArray(brand?.brandNames) ? brand.brandNames.map(clean) : []),
    ...(Array.isArray(brand?.extraBrandNames)
      ? brand.extraBrandNames.map(clean)
      : []),
  ].filter(Boolean);

  // Filter out placeholders like "EIN 12-3456789"
  const filtered = possible.filter(
    (nm) => !/^ein\s*\d/i.test(nm)
  );

  // If nothing usable, fall back to something deterministic:
  if (!filtered.length) {
    const ein = formatEin(readAnyEin(brand));
    if (ein) return [`EIN ${ein}`];
    return ['Unassigned'];
  }

  return Array.from(new Set(filtered));
}

/**
 * Turn brand docs => cards grouped by bannerName
 *
 * Input: docsMap: { [brandId]: fullBrandDoc }
 * Output: Array of card objects shaped for dashboard rows (minus locations)
 */
function aggregateBannerCardsFromDocs(docsMap = {}, summariesMap = {}) {
  // Group by canonical brand identity (EIN/Brand), not banner text.
  // This prevents duplicate cards when the same brand is present under multiple map keys.
  const byComposite = new Map();

  Object.entries(docsMap).forEach(([bId, brand]) => {
    if (!brand) return;
    // `brandId` can be shared across EIN enrollments and `/brand/:id` can return
    // a doc whose `_id` differs from the requested key; always anchor on map key first.
    const canonicalBrandKey = normalizeBrandKey(bId || brand?._id || brand?.brandId);
    const mapKey = normalizeBrandKey(bId);
    if (!canonicalBrandKey || !mapKey) return;

    const bannerNames = getBannerNames(brand);
    // choose the primary/first usable name for the card
    const bannerName = bannerNames[0] || brand?.name || brand?.dbaName || 'Unassigned';

    const einDigits = normalizeEin(readAnyEin(brand));
    const einFormatted = einDigits ? formatEin(einDigits) : '';

    const coverageExpected = Number(brand?.coverageRollup?.expectedCount || 0);
    const coverageEnrolled = Number(brand?.coverageRollup?.enrolledCount || 0);

    // one card per canonical brand
    const key = canonicalBrandKey;

    if (!byComposite.has(key)) {
      byComposite.set(key, {
        key,
        bannerName,
        // keep concrete docsMap keys so downstream location lookup works
        brandIds: new Set([mapKey]),
        eins: new Set(),
        categories: new Set(),
        lifecycleStatuses: new Set(),
        createdAts: [],
        logos: new Set(),
        tipEligibleAny: false,
        coverageExpected: 0,
        coverageEnrolled: 0,
        lifetimeCents: 0,
        last30dCents: 0,
        hasContributionData: false,
        contributionPercents: new Set(),
        _metricsApplied: false,
      });
    }

    const bucket = byComposite.get(key);
    bucket.brandIds.add(mapKey);
    if (einFormatted) bucket.eins.add(einFormatted);
    if (brand.category) bucket.categories.add(brand.category);
    if (brand.lifecycleStatus) bucket.lifecycleStatuses.add(brand.lifecycleStatus);
    else if (brand.status) bucket.lifecycleStatuses.add(brand.status);

    if (!bucket._metricsApplied) {
      if (Number.isFinite(Number(brand.baseContributionPercent))) {
        bucket.contributionPercents.add(Number(brand.baseContributionPercent));
      }
      if (brand.createdAt) bucket.createdAts.push(brand.createdAt);

      const logo = safeBrandLogo(brand);
      if (logo) bucket.logos.add(logo);

      const summary = summariesMap?.[canonicalBrandKey] || summariesMap?.[bId];
      if (summary?.tipDecision?.decision === 'eligible') {
        bucket.tipEligibleAny = true;
      } else if (brand.tipPolicy?.defaultEligible) {
        bucket.tipEligibleAny = true;
      }
      if (summary?.performance?.contributions) {
        const { lifetimeCents = 0, last30dCents = 0 } = summary.performance.contributions;
        bucket.lifetimeCents += Number(lifetimeCents) || 0;
        bucket.last30dCents += Number(last30dCents) || 0;
        bucket.hasContributionData = true;
      }

      bucket.coverageExpected += coverageExpected;
      bucket.coverageEnrolled += coverageEnrolled;
      bucket._metricsApplied = true;
    }
  });

  // finalize cards
  const cards = Array.from(byComposite.values()).map((bucket) => {
    const einDisplay = bucket.eins.size ? Array.from(bucket.eins).join(' • ') : 'Unassigned';

    // status priority
    const statuses = Array.from(bucket.lifecycleStatuses);
    let statusLabel = 'Onboarding';
    if (statuses.includes('active')) statusLabel = 'Active';
    else if (statuses.includes('verified')) statusLabel = 'Verified';
    else if (statuses.length === 1) statusLabel = titleCase(statuses[0]);

    // earliest createdAt
    const regMs = bucket.createdAts
      .map((d) => new Date(d).getTime())
      .filter((t) => !Number.isNaN(t));
    const regDateRaw = regMs.length ? new Date(Math.min(...regMs)) : null;

    const category = Array.from(bucket.categories)[0] || 'Uncategorized';
    const logoUrl = Array.from(bucket.logos)[0] || '';

    return {
      id: bucket.key, // unique per (name + brandId)
      displayName: bucket.bannerName,
      einTaxIdDisplay: einDisplay,
      category,
      lifecycleStatusLabel: statusLabel,
      regDateRaw,
      coverageExpected: bucket.coverageExpected,
      coverageEnrolled: bucket.coverageEnrolled,
      logoUrl: logoUrl || DEFAULT_BRAND_ICON,
      tipEligible: bucket.tipEligibleAny,
      contributions: {
        lifetimeCents: bucket.lifetimeCents,
        last30dCents: bucket.last30dCents,
        hasData: bucket.hasContributionData,
      },
      baseContributionPercents: Array.from(bucket.contributionPercents),
      brandIds: Array.from(bucket.brandIds), // single brandId in practice
    };
  });

  cards.sort((a, b) => a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()));
  return cards;
}

function normalizeBrandKey(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (typeof value.$oid === 'string') return value.$oid;
    if (typeof value.toString === 'function') {
      const v = value.toString();
      if (v && v !== '[object Object]') return v;
    }
    if (typeof value.id === 'string') return value.id;
  }
  return String(value);
}

/**
 * Build all store locations across all brandIds for a given card,
 * dedupe by store id / placeId,
 * and annotate active vs temp_excluded and tipEligible.
 */
function buildLocationsForBannerCard(card, allDocs, tipEligibleKeySet) {
  const seen = new Set();
  const out = [];

  (card.brandIds || []).forEach((bId) => {
    const doc = allDocs[bId];
    if (!doc) return;
    const stores = Array.isArray(doc?.stores) ? doc.stores : [];

    stores.forEach((s) => {
      if (!(s && (s.status === 'active' || s.status === 'temp_excluded'))) {
        return;
      }

      const candidateKeys = [
        s._id?.toString?.(),
        s.id?.toString?.(),
        s.locationId,
        s.placeId,
        s.googlePlaceId,
      ]
        .map((k) => (k !== undefined && k !== null ? String(k) : ''))
        .filter(Boolean);

      const locId = candidateKeys[0] || Math.random().toString(36).slice(2);
      if (seen.has(locId)) return;
      seen.add(locId);

      const tipEligible = candidateKeys.some((key) =>
        tipEligibleKeySet.has(key)
      );

      const statusCode =
        s.status === 'temp_excluded' ? 'temp_excluded' : 'active';

      const exclusionReason =
        s.exclusionReason ||
        (s.status === 'temp_excluded' && s.reopenDate
          ? `Reopens ${new Date(s.reopenDate).toLocaleDateString()}`
          : '');

      out.push({
        id: locId,
        address:
          s.address ||
          s.formattedAddress ||
          s.marketKey ||
          'Location',
        statusCode,
        statusLabel: statusCode === 'active' ? 'Enrolled' : 'Temp Excluded',
        exclusionReason,
        tipEligible,
      });
    });
  });

  return out;
}

const getDefaultExcludeDate = () => {
  const next = new Date();
  next.setDate(next.getDate() + 30);
  return next.toISOString().slice(0, 10);
};

const getAreaId = (area = {}) =>
  area._id ||
  area.id ||
  area.operatingAreaId ||
  area.operating_area_id ||
  area.value ||
  '';

const getCandidateKey = (candidate) =>
  [
    candidate?._id,
    candidate?.placeId,
    candidate?.googlePlaceId,
    candidate?.locationId,
    candidate?.id,
    candidate?.name,
    candidate?.formattedAddress,
  ]
    .map((v) => (v !== undefined && v !== null ? String(v) : ''))
    .find((v) => v.length > 0) || '';

const TIP_TYPE_SET = new Set([
  'bar',
  'cafe',
  'bakery',
  'night_club',
  'coffee_shop',
  'beauty_salon',
  'hair_care',
  'nail_salon',
  'spa',
  'gym',
  'barber_shop',
]);

const detectTipEligible = (types = []) => {
  if (!Array.isArray(types)) return false;
  const normalized = types.map((type) => String(type).toLowerCase());
  if (normalized.some((type) => type.includes('fast_food_restaurant') || type.includes('meal_takeaway') || type.includes('meal_delivery'))) {
    return false;
  }
  return normalized.some((type) => TIP_TYPE_SET.has(type));
};

const STATE_RE = /^[A-Z]{2}$/;

const deriveCriteriaTokens = (input) => {
  const raw = String(input || '').trim();
  if (!raw) return [];

  const zipMatches = Array.from(new Set(raw.match(/\b\d{5}\b/g) || []));
  if (zipMatches.length) {
    return [{ type: 'zip', zips: zipMatches }];
  }

  const cityStateMatch = raw.match(/^(.+?)[,\s]+([A-Za-z]{2})$/);
  if (cityStateMatch) {
    const city = titleCase(cityStateMatch[1]);
    const state = cityStateMatch[2].toUpperCase();
    if (city && STATE_RE.test(state)) {
      return [{ type: 'city', city, state }];
    }
  }

  const stateOnly = raw.replace(/\s+/g, '').toUpperCase();
  if (STATE_RE.test(stateOnly)) {
    return [{ type: 'state', state: stateOnly }];
  }

  if (raw.length) {
    return [{ type: 'city', city: titleCase(raw) }];
  }

  return [];
};


const AddStoreModal = ({
  brandId: defaultBrandId,
  onClose,
  refreshBrandData,
  ensureExclusionReasons,
  exclusionReasons,
  reasonsLoading,
  fallbackReasons,
  einOptions = [],
}) => {
  /**
   * @typedef {Object} OperatingArea
   * @property {string=} _id
   * @property {string=} id
   * @property {string=} operatingAreaId
   * @property {string=} operating_area_id
   * @property {string=} value
   * @property {string=} label
   * @property {Array<string>=} criteria
   */
  const [areas, setAreas] = useState(/** @type {OperatingArea[]} */([]));
  const [areasLoading, setAreasLoading] = useState(false);
  const [areasError, setAreasError] = useState('');
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [areaSelection, setAreaSelection] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [infoMessage, setInfoMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState('');
  const [discoveringContext, setDiscoveringContext] = useState('existing');
  const [newAreaForm, setNewAreaForm] = useState({ label: '' });
  const [newAreaSaving, setNewAreaSaving] = useState(false);
  const [newAreaError, setNewAreaError] = useState('');
  const [excludeState, setExcludeState] = useState({
    open: false,
    candidate: null,
    reason: '',
    date: getDefaultExcludeDate(),
    note: '',
  });
  const [excludeError, setExcludeError] = useState('');
  const [excludeSaving, setExcludeSaving] = useState(false);

  const [selectedEinKey, setSelectedEinKey] = useState('');
  const [selectedBrandKey, setSelectedBrandKey] = useState('');

  const computeCriteriaFromInput = useCallback(deriveCriteriaTokens, []);

  const availableReasons =
    Array.isArray(exclusionReasons) && exclusionReasons.length
      ? exclusionReasons
      : fallbackReasons || [];

  const selectedEin = useMemo(
    () => einOptions.find((option) => option.key === selectedEinKey) || null,
    [einOptions, selectedEinKey]
  );

  const selectedBrand = useMemo(
    () =>
      selectedEin?.brands.find((brand) => brand.key === selectedBrandKey) ||
      null,
    [selectedEin, selectedBrandKey]
  );

  const selectedBrandLabel = useMemo(() => selectedBrand?.label || '', [selectedBrand]);

  const activeBrandId = selectedBrand?.brandId || null;
  const gatingComplete = !!activeBrandId;
  const canProceed = gatingComplete;
  const [pendingAreaDraft, setPendingAreaDraft] = useState(null);

  useEffect(() => {
    if (!einOptions.length) {
      if (selectedEinKey) setSelectedEinKey('');
      if (selectedBrandKey) setSelectedBrandKey('');
      return;
    }

    const einExists = einOptions.some((option) => option.key === selectedEinKey);
    if (!einExists) {
      let nextEinKey = '';
      if (defaultBrandId) {
        const match = einOptions.find((option) =>
          option.brands.some((brand) => brand.brandId === defaultBrandId)
        );
        if (match) {
          nextEinKey = match.key;
        }
      }
      if (!nextEinKey) {
        nextEinKey = einOptions[0].key;
      }
      setSelectedEinKey(nextEinKey);
      // brand selection will be handled in the next effect
    }
  }, [einOptions, defaultBrandId, selectedEinKey, selectedBrandKey]);

  useEffect(() => {
    if (!selectedEin) {
      if (selectedBrandKey) setSelectedBrandKey('');
      return;
    }

    const brandExists = selectedEin.brands.some(
      (brand) => brand.key === selectedBrandKey
    );
    if (!brandExists) {
      let nextBrandKey = '';
      if (defaultBrandId) {
        const matchBrand = selectedEin.brands.find(
          (brand) => brand.brandId === defaultBrandId
        );
        if (matchBrand) {
          nextBrandKey = matchBrand.key;
        }
      }
      if (!nextBrandKey && selectedEin.brands.length === 1) {
        nextBrandKey = selectedEin.brands[0].key;
      }
      setSelectedBrandKey(nextBrandKey);
    }
  }, [selectedEin, selectedBrandKey, defaultBrandId]);

  const buildCriteriaFromForm = useCallback(() => {
    const criteria = computeCriteriaFromInput(newAreaForm.label);
    return { criteria };
  }, [computeCriteriaFromInput, newAreaForm.label]);

  const mapCandidates = useCallback(
    (list = []) =>
      list
        .map((item) => {
          const rawTypes = Array.isArray(item.types) ? item.types : [];
          const primaryTypePool =
            rawTypes && rawTypes.length
              ? rawTypes
              : item.primaryType
              ? [item.primaryType]
              : [];
          const hours =
            Array.isArray(item.hoursText) && item.hoursText.length
              ? item.hoursText
              : Array.isArray(item.currentOpeningHours?.weekdayDescriptions)
              ? item.currentOpeningHours.weekdayDescriptions
              : Array.isArray(item.regularOpeningHours?.weekdayDescriptions)
              ? item.regularOpeningHours.weekdayDescriptions
              : [];
          const location = item.location || {};
          const lat =
            typeof item.latitude === 'number'
              ? item.latitude
              : typeof location.latitude === 'number'
              ? location.latitude
              : undefined;
          const lng =
            typeof item.longitude === 'number'
              ? item.longitude
              : typeof location.longitude === 'number'
              ? location.longitude
              : undefined;
          const ratingValue =
            typeof item.rating === 'number'
              ? item.rating
              : typeof item.googleCache?.rating === 'number'
              ? item.googleCache.rating
              : undefined;
          const ratingCount =
            typeof item.userRatingsTotal === 'number'
              ? item.userRatingsTotal
              : typeof item.userRatingCount === 'number'
              ? item.userRatingCount
              : typeof item.googleCache?.userRatingsTotal === 'number'
              ? item.googleCache.userRatingsTotal
              : undefined;
          const price =
            typeof item.priceLevel === 'number'
              ? item.priceLevel
              : typeof item.googleCache?.priceLevel === 'number'
              ? item.googleCache.priceLevel
              : undefined;
          const openNowValue =
            typeof item.openNow === 'boolean'
              ? item.openNow
              : typeof item.currentOpeningHours?.openNow === 'boolean'
              ? item.currentOpeningHours.openNow
              : typeof item.googleCache?.openNow === 'boolean'
              ? item.googleCache.openNow
              : undefined;

            return {
              ...item,
              name: item.name || item.displayName || item.title || 'Store',
              types: rawTypes,
              tipEligible: detectTipEligible(primaryTypePool),
              iconMaskBaseUri:
                item.iconMaskBaseUri || item.icon_mask_base_uri || '',
              iconBackgroundColor:
                item.iconBackgroundColor ||
                item.icon_background_color ||
                '',
              phone:
                item.phone ||
                item.nationalPhoneNumber ||
                item.internationalPhoneNumber ||
                '',
            website: item.website || item.websiteUri || '',
            businessStatus: item.businessStatus || '',
            hoursText: hours,
            latitude: lat,
            longitude: lng,
            rating: ratingValue,
            userRatingsTotal: ratingCount,
            priceLevel: price,
            openNow: openNowValue,
          };
        })
        .filter(
  (item) =>
    !(
      String(item.primaryType || '').toLowerCase() === 'locality' ||
      (Array.isArray(item.types) &&
        item.types.some((t) => String(t).toLowerCase() === 'locality'))
    ) &&
    !!(item.placeId || item.googlePlaceId || item.locationId) &&
    getCandidateKey(item)
),
    []
  );

  const loadAreas = useCallback(async () => {
    if (!activeBrandId) return [];
    setAreasLoading(true);
    setAreasError('');
    try {
      const data = await apiFetch(
        `/brand/operating-areas?brandId=${encodeURIComponent(activeBrandId)}`
      );
      const list = Array.isArray(data?.areas) ? data.areas : Array.isArray(data) ? data : [];
      setAreas(list);
      return list;
    } catch (err) {
      setAreas([]);
      setSelectedAreaId('');
      setAreaSelection('');
      setAreasError(err.message || 'Unable to load operating areas.');
      return [];
    } finally {
      setAreasLoading(false);
    }
  }, [activeBrandId]);

  const resolveAreaLabel = useCallback(
    (areaId) => {
      if (areaId) {
        const match = areas.find((area) => getAreaId(area) === areaId);
        if (match) return match.label || match.name || '';
      }
      if (pendingAreaDraft?.label) return pendingAreaDraft.label;
      if (areaSelection) {
        const fallback = areas.find((area) => getAreaId(area) === areaSelection);
        if (fallback) return fallback.label || fallback.name || '';
      }
      return '';
    },
    [areaSelection, areas, pendingAreaDraft]
  );

  const buildAddLocationPayload = useCallback(
    (candidate, areaId) => {
      const areaLabel =
        resolveAreaLabel(areaId) ||
        candidate.searchAreaLabel ||
        candidate.marketKey ||
        '';
      const lat =
        typeof candidate.latitude === 'number'
          ? candidate.latitude
          : typeof candidate.location?.latitude === 'number'
          ? candidate.location.latitude
          : undefined;
      const lng =
        typeof candidate.longitude === 'number'
          ? candidate.longitude
          : typeof candidate.location?.longitude === 'number'
          ? candidate.location.longitude
          : undefined;
      const hours =
        Array.isArray(candidate.hoursText) && candidate.hoursText.length
          ? candidate.hoursText
          : undefined;
      const types =
        Array.isArray(candidate.types) && candidate.types.length
          ? candidate.types
          : undefined;

      const payload = {
        brandId: activeBrandId,
        placeId: candidate.placeId,
        source: 'google',
        sourceConfidence: 1,
        name: candidate.name || candidate.displayName || 'Store',
        manualAddress: candidate.address || undefined,
        formattedAddress:
          candidate.formattedAddress ||
          candidate.address ||
          candidate.vicinity ||
          '',
        marketKey: areaLabel || undefined,
        channel: 'pos',
        status: 'active',
        isActive: true,
        tipEligible: !!candidate.tipEligible,
        businessStatus: candidate.businessStatus || undefined,
        primaryType:
          candidate.primaryType ||
          (types && types.length ? types[0] : undefined),
        iconMaskBaseUri: candidate.iconMaskBaseUri || undefined,
        iconBackgroundColor: candidate.iconBackgroundColor || undefined,
        phone: candidate.phone || undefined,
        website: candidate.website || undefined,
        latitude: typeof lat === 'number' ? lat : undefined,
        longitude: typeof lng === 'number' ? lng : undefined,
        hours,
        types,
        rating:
          typeof candidate.rating === 'number' ? candidate.rating : undefined,
        userRatingsTotal:
          typeof candidate.userRatingsTotal === 'number'
            ? candidate.userRatingsTotal
            : undefined,
        priceLevel:
          typeof candidate.priceLevel === 'number'
            ? candidate.priceLevel
            : undefined,
        openNow:
          typeof candidate.openNow === 'boolean'
            ? candidate.openNow
            : undefined,
        mcc: candidate.mcc || undefined,
      };

      if (
        typeof payload.latitude === 'number' &&
        typeof payload.longitude === 'number'
      ) {
        payload.location = {
          latitude: payload.latitude,
          longitude: payload.longitude,
        };
      }

      return payload;
    },
    [activeBrandId, resolveAreaLabel]
  );

  const handleRequestClose = useCallback(() => {
    setPendingAreaDraft(null);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!defaultBrandId) {
      handleRequestClose();
    }
  }, [defaultBrandId, handleRequestClose]);

  useEffect(() => {
    setAreas([]);
    setSelectedAreaId('');
    setAreaSelection('');
    setCandidates([]);
    setInfoMessage('');
    setActionError('');
    setActionLoadingId('');
    setDiscoverError('');
    setDiscovering(false);
    setAreasError('');
    setNewAreaError('');
    setNewAreaSaving(false);
    setNewAreaForm({ label: '' });
    setExcludeState({
      open: false,
      candidate: null,
      reason: '',
      date: getDefaultExcludeDate(),
      note: '',
    });
    setExcludeError('');
    setExcludeSaving(false);
    setPendingAreaDraft(null);
    setDiscoveringContext('existing');

    if (activeBrandId) {
      loadAreas();
    }
  }, [activeBrandId, loadAreas]);

  const runDiscover = useCallback(
    async (
      areaId,
      { fromNewArea = false, criteriaOverride = null, labelOverride = '' } = {}
    ) => {
      if (!activeBrandId && !fromNewArea) return { normalized: [], raw: [] };

      const selectedArea = areaId
        ? areas.find((area) => getAreaId(area) === areaId) || null
        : null;
      const label = fromNewArea
        ? labelOverride || newAreaForm.label || ''
        : selectedArea?.label || newAreaForm.label || '';

      const storedCriteria = Array.isArray(selectedArea?.criteria) ? selectedArea.criteria : [];
      const criteriaForSearch = Array.isArray(criteriaOverride) && criteriaOverride.length
        ? criteriaOverride
        : storedCriteria.length
          ? storedCriteria
          : computeCriteriaFromInput(label);

      if (!Array.isArray(criteriaForSearch) || !criteriaForSearch.length) {
        setCandidates([]);
        setInfoMessage('Add more detail (city, ZIP, or state) to search for stores.');
        setDiscovering(false);
        return { normalized: [], raw: [] };
      }

      if (fromNewArea) {
        setDiscoveringContext('new');
      } else {
        setDiscoveringContext('existing');
      }
      setDiscovering(true);
      setDiscoverError('');
      setActionError('');
      setInfoMessage('');

      try {
        const resp = await apiFetch('/brand/operating-area/preview', {
          method: 'POST',
          body: JSON.stringify({
            brandId: activeBrandId,
            searchName: selectedBrandLabel,
            criteria: criteriaForSearch,
          }),
        });

        if (!fromNewArea && Array.isArray(resp?.errors) && resp.errors.length) {
          setActionError(resp.errors[0]);
        }

        const list = Array.isArray(resp?.candidates)
          ? resp.candidates
          : Array.isArray(resp)
            ? resp
            : [];
        const sourceAreaId = selectedArea ? getAreaId(selectedArea) : '';
        const normalized = mapCandidates(list).map((candidate) => {
          const marketLabel = candidate.marketKey || label || '';
          return {
            ...candidate,
            marketKey: marketLabel,
            searchAreaLabel: label,
            sourceAreaId,
          };
        });

        if (!fromNewArea) {
          setCandidates(normalized);
          if (!normalized.length) {
            setInfoMessage('All stores in this operating area are already enrolled.');
          } else {
            setInfoMessage('');
          }
        }

        return { normalized, raw: list, response: resp };
      } catch (err) {
        if (!fromNewArea) {
          setCandidates([]);
          setDiscoverError(err.message || 'Failed to discover stores.');
        }
        throw err;
      } finally {
        setDiscovering(false);
      }
    },
    [activeBrandId, areas, computeCriteriaFromInput, mapCandidates, newAreaForm.label, selectedBrandLabel]
  );

  const handleAreaSelectionChange = useCallback(
    (value) => {
      const next = value || '';
      setAreaSelection(next);
      setInfoMessage('');
      setActionError('');
      setDiscoverError('');
      setPendingAreaDraft(null);
      if (!next) {
        setSelectedAreaId('');
        setCandidates([]);
        setDiscoveringContext('existing');
        return;
      }

      if (!canProceed) {
        setSelectedAreaId(next);
        return;
      }

      const selected = areas.find((area) => getAreaId(area) === next);
      const storedCriteria = Array.isArray(selected?.criteria) ? selected.criteria : [];
      const fallbackCriteria = computeCriteriaFromInput(selected?.label || '');
      const criteriaForSearch = storedCriteria.length ? storedCriteria : fallbackCriteria;
      const hasCriteria = Array.isArray(criteriaForSearch) && criteriaForSearch.length > 0;

      setSelectedAreaId(next);
      if (!hasCriteria) {
        setCandidates([]);
        setInfoMessage('Add more detail (city, ZIP, or state) and press "Search Stores".');
        setDiscoveringContext('existing');
        return;
      }

      runDiscover(next, { criteriaOverride: criteriaForSearch });
    },
    [areas, canProceed, runDiscover, computeCriteriaFromInput]
  );

  useEffect(() => {
    if (!canProceed) {
      if (areaSelection) setAreaSelection('');
      if (selectedAreaId) setSelectedAreaId('');
      return;
    }

    if (!areaSelection) {
      if (selectedAreaId) setSelectedAreaId('');
      return;
    }

    const exists = areas.some((area) => getAreaId(area) === areaSelection);
    if (!exists) {
      if (areaSelection) setAreaSelection('');
      if (selectedAreaId) setSelectedAreaId('');
      return;
    }

    if (selectedAreaId !== areaSelection) {
      setSelectedAreaId(areaSelection);
    }
  }, [areas, areaSelection, canProceed, selectedAreaId]);

  const handleSearchStores = async () => {
    if (!activeBrandId) {
      setNewAreaError('Select an EIN and brand first.');
      return;
    }
    const label = newAreaForm.label.trim();
    if (!label) {
      setNewAreaError('Name the operating area.');
      return;
    }
    const { criteria } = buildCriteriaFromForm();
    if (!Array.isArray(criteria) || !criteria.length) {
      setNewAreaError('Add more detail (city, ZIP, or state).');
      return;
    }
    setNewAreaSaving(true);
    setNewAreaError('');
    setDiscoverError('');
    setActionError('');
    setInfoMessage('');
    setPendingAreaDraft({
      label,
      criteria,
      brandId: activeBrandId,
      areaId: '',
    });
    setAreaSelection('');
    setSelectedAreaId('');
    try {
      const resp = await runDiscover(null, {
        fromNewArea: true,
        criteriaOverride: criteria,
        label,
      });

      const list = Array.isArray(resp?.normalized) ? resp.normalized : [];
      setCandidates(list);
      if (!list.length) {
        setInfoMessage('No stores found for this operating area yet.');
      } else {
        setInfoMessage('');
      }
    } catch (err) {
      setNewAreaError(err.message || 'Failed to search stores.');
      setCandidates([]);
      setPendingAreaDraft(null);
    } finally {
      setNewAreaSaving(false);
    }
  };

  const ensureAreaForAction = useCallback(async () => {
    if (selectedAreaId) {
      return { areaId: selectedAreaId, created: false };
    }
    if (
      !pendingAreaDraft ||
      !pendingAreaDraft.label ||
      !Array.isArray(pendingAreaDraft.criteria) ||
      !pendingAreaDraft.criteria.length ||
      pendingAreaDraft.brandId !== activeBrandId
    ) {
      return { areaId: '', created: false };
    }
    if (pendingAreaDraft.areaId) {
      return { areaId: pendingAreaDraft.areaId, created: false };
    }

    const payload = {
      brandId: activeBrandId,
      label: pendingAreaDraft.label,
      type: 'criteria',
      criteria: pendingAreaDraft.criteria,
    };
    const created = await apiFetch('/brand/operating-area', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const newId = getAreaId(created);
    if (!newId) {
      throw new Error('Unable to create operating area for this search.');
    }
    setPendingAreaDraft((prev) =>
      prev
        ? {
            ...prev,
            areaId: newId,
          }
        : prev
    );
    return { areaId: newId, created: true };
  }, [activeBrandId, pendingAreaDraft, selectedAreaId]);

  const rollbackAreaCreation = useCallback(
    async (areaId) => {
      if (!areaId || !activeBrandId) return;
      try {
        await apiFetch(`/brand/operating-area/${encodeURIComponent(areaId)}`, {
          method: 'DELETE',
          body: JSON.stringify({ brandId: activeBrandId }),
        });
      } catch (err) {
        console.warn('Unable to roll back operating area', err);
      } finally {
        setPendingAreaDraft((prev) => {
          if (prev?.areaId === areaId) {
            const { areaId: _ignored, ...rest } = prev;
            return { ...rest, areaId: '' };
          }
          return prev;
        });
      }
    },
    [activeBrandId]
  );

  const removeCandidateByKey = (key) => {
    setCandidates((prev) => {
      const next = prev.filter((item) => getCandidateKey(item) !== key);
      if (!next.length) {
        setInfoMessage('No unregistered stores found in this area.');
      }
      return next;
    });
  };

  const handleEnrollCandidate = async (candidate) => {
    if (!activeBrandId) return;
    const key = getCandidateKey(candidate);
    if (!key) return;

    setActionLoadingId(key);
    setActionError('');

    let areaContext = { areaId: selectedAreaId, created: false };
    try {
      areaContext = await ensureAreaForAction();
      if (!areaContext.areaId) {
        setActionError(
          'Select an existing operating area or search for a new one first.'
        );
        return;
      }

      const suggestionId = candidate?.suggestionId || candidate?._id;
      if (suggestionId) {
        await apiFetch('/brand/location/adopt', {
          method: 'POST',
          body: JSON.stringify({
            brandId: activeBrandId,
            suggestionId,
            tipEligible: detectTipEligible(candidate?.types || []),
            areaId: areaContext.areaId,
          }),
        });
      } else {
        const payload = buildAddLocationPayload(candidate, areaContext.areaId);
        if (!payload.placeId) {
          setActionError('Unable to enroll this store (missing place ID).');
          return;
        }
        await apiFetch('/brand/location', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }

      removeCandidateByKey(key);
      if (areaContext.created) {
        const fetched = await loadAreas();
        if (
          Array.isArray(fetched) &&
          fetched.some((area) => getAreaId(area) === areaContext.areaId)
        ) {
          setAreaSelection(areaContext.areaId);
          setSelectedAreaId(areaContext.areaId);
        }
        setPendingAreaDraft(null);
      }
      setInfoMessage('Store enrolled successfully.');
      refreshBrandData();
    } catch (err) {
      if (areaContext.created) {
        await rollbackAreaCreation(areaContext.areaId);
      }
      setActionError(err.message || 'Failed to enroll this store.');
    } finally {
      setActionLoadingId('');
    }
  };

  const handleIgnoreCandidate = async (candidate) => {
    if (!activeBrandId) return;
    const key = getCandidateKey(candidate);
    if (!key) return;
    const suggestionId = candidate?.suggestionId;
    setActionLoadingId(key);
    setActionError('');
    try {
      if (suggestionId) {
        await apiFetch('/brand/drift/ignore', {
          method: 'POST',
          body: JSON.stringify({ brandId: activeBrandId, suggestionId }),
        });
      }
      removeCandidateByKey(key);
      refreshBrandData();
    } catch (err) {
      setActionError(err.message || 'Failed to dismiss this store.');
    } finally {
      setActionLoadingId('');
    }
  };

  const openTempExcludeFor = async (candidate) => {
    if (!activeBrandId) return;
    setExcludeError('');
    setExcludeSaving(false);
    setExcludeState({
      open: true,
      candidate,
      reason: '',
      date: getDefaultExcludeDate(),
      note: '',
    });
    await ensureExclusionReasons();
  };

  const handleExcludeFieldChange = (field, value) => {
    setExcludeState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const closeExcludeModal = () => {
    setExcludeState({
      open: false,
      candidate: null,
      reason: '',
      date: getDefaultExcludeDate(),
      note: '',
    });
    setExcludeError('');
  };

  const handleSubmitExclude = async (event) => {
    event?.preventDefault?.();
    if (!activeBrandId) return;
    const candidate = excludeState.candidate;
    if (!candidate) return;
    if (!excludeState.reason) {
      setExcludeError('Select a reason for the exclusion.');
      return;
    }
    const until = new Date(excludeState.date);
    if (!excludeState.date || Number.isNaN(until.getTime()) || until <= new Date()) {
      setExcludeError('Exclude-until must be a future date.');
      return;
    }
    const key = getCandidateKey(candidate);
    setExcludeSaving(true);
    setExcludeError('');
    let areaContext = { areaId: selectedAreaId, created: false };
    try {
      areaContext = await ensureAreaForAction();
      if (!areaContext.areaId) {
        setExcludeError('Select an existing operating area or search for a new one first.');
        return;
      }
      await apiFetch('/brand/location/temp-exclude', {
        method: 'POST',
        body: JSON.stringify({
          brandId: activeBrandId,
          suggestionId: candidate?.suggestionId || undefined,
          placeId: candidate?.placeId,
          formattedAddress: candidate?.formattedAddress || candidate?.address || '',
          name: candidate?.name || candidate?.displayName || '',
          marketKey: candidate?.marketKey || undefined,
          reason: excludeState.reason,
          excludeUntil: until.toISOString(),
          notes: excludeState.note ? excludeState.note.trim() : undefined,
          areaId: areaContext.areaId,
        }),
      });
      if (areaContext.created) {
        setPendingAreaDraft(null);
        const fetched = await loadAreas();
        if (Array.isArray(fetched) && fetched.some((area) => getAreaId(area) === areaContext.areaId)) {
          setAreaSelection(areaContext.areaId);
          setSelectedAreaId(areaContext.areaId);
        }
      }
      if (key) removeCandidateByKey(key);
      refreshBrandData();
      closeExcludeModal();
    } catch (err) {
      if (areaContext.created) {
        await rollbackAreaCreation(areaContext.areaId);
      }
      setExcludeError(err.message || 'Failed to temp-exclude this location.');
    } finally {
      setExcludeSaving(false);
    }
  };

  const formatBusinessStatus = (status, openNow) => {
    if (!status && typeof openNow !== 'boolean') return '';
    const base = status
      ? status
          .toString()
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : '';
    if (typeof openNow === 'boolean') {
      const suffix = openNow ? 'Open Now' : 'Closed Now';
      return base ? `${base} • ${suffix}` : suffix;
    }
    return base;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  const formatPriceLevel = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value) || value < 0) {
      return '';
    }
    const level = Math.min(Math.floor(value), 4);
    return '$'.repeat(level + 1);
  };

  const formatWebsiteHost = (url) => {
    if (!url) return '';
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, '');
      return host || url;
    } catch {
      return url;
    }
  };

  const formatPrimaryType = (value) => {
    if (!value) return '';
    return titleCase(String(value).replace(/_/g, ' '));
  };

  const renderTipPill = (flag) => (
    <span
      style={{
        ...styles.tipAutoBadgeBase,
        ...(flag ? styles.tipAutoBadgeLikely : styles.tipAutoBadgeUnlikely),
      }}
      title="Auto-detected from Google types"
    >
      {flag ? 'Tip Likely' : 'Tips Unlikely'}
    </span>
  );

  const hasEinOptions = einOptions.length > 0;
  const canSaveNewArea = newAreaForm.label.trim().length > 0;
  return (
    <div style={styles.modalBackdrop} onClick={handleRequestClose}>
      <div
        style={styles.modalLarge}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeaderRow}>
          <div style={styles.modalTitle}>Add Store</div>
          <div style={styles.modalSubtitle}>
            Discover and enroll stores by operating area.
          </div>
        </div>

        <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
          <label style={styles.modalLabel} htmlFor="add-store-ein">
            Registered EIN
          </label>
          <select
            id="add-store-ein"
            style={styles.modalSelect}
            value={selectedEinKey}
            onChange={(e) => {
              setSelectedEinKey(e.target.value);
              setSelectedBrandKey('');
            }}
          >
            <option value="">Select an EIN…</option>
            {einOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>

          <label style={styles.modalLabel} htmlFor="add-store-brand">
            Public Facing Brand
          </label>
          <select
            id="add-store-brand"
            style={styles.modalSelect}
            value={selectedBrandKey}
            onChange={(e) => setSelectedBrandKey(e.target.value)}
            disabled={!selectedEin || !(selectedEin?.brands || []).length}
          >
            <option value="">Select a brand…</option>
            {(selectedEin?.brands || []).map((brand) => (
              <option key={brand.key} value={brand.key}>
                {brand.label}
              </option>
            ))}
          </select>
        </div>

        {!hasEinOptions ? (
          <div style={styles.modalInfoText}>
            No registered EINs are available for this account yet. Add a
            business or wait for data to finish loading.
          </div>
        ) : !canProceed ? (
          <div style={styles.modalInfoText}>
            Select an EIN and brand to continue.
          </div>
        ) : null}

        <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
          <label style={styles.modalLabel} htmlFor="add-store-area">
            Existing Operating Areas
          </label>
          <select
            id="add-store-area"
            style={styles.modalSelect}
            value={areaSelection}
            onChange={(e) => handleAreaSelectionChange(e.target.value)}
            disabled={!canProceed}
          >
            <option value="">
              {areasLoading
                ? 'Loading operating areas…'
                : 'Select Existing Area…'}
            </option>
            {areas.map((area) => {
              const id = getAreaId(area);
              return (
                <option key={id} value={id}>
                  {area.label || area.name || id || 'Area'}
                </option>
              );
            })}
          </select>
          {canProceed && areasLoading ? (
            <div style={styles.modalInfoText}>Loading operating areas…</div>
          ) : null}
          {canProceed && areasError ? (
            <div style={styles.modalError}>{areasError}</div>
          ) : null}
          {canProceed && !areasLoading && !areas.length ? (
            <div style={styles.modalInfoText}>
              No operating areas yet—use "Add New Operating Area" to create one.
            </div>
          ) : null}
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          <label style={styles.modalLabel} htmlFor="new-area-label">
            Add New Operating Area
          </label>
          <input
            id="new-area-label"
            type="text"
            style={styles.modalInput}
            placeholder="Search by city, ZIP, or state"
            value={newAreaForm.label}
            onChange={(e) => {
              const { value } = e.target;
              setNewAreaForm((prev) => ({ ...prev, label: value }));
              if (newAreaError) setNewAreaError('');
              if (infoMessage) setInfoMessage('');
              if (discoverError) setDiscoverError('');
            }}
            disabled={!canProceed}
          />
          <div style={styles.modalInfoText}>
            Try typing a city, ZIP, or 2-letter state to discover new stores.
          </div>
          {newAreaError && <div style={styles.modalError}>{newAreaError}</div>}
        </div>

        <div style={styles.discoverActionsRow}>
          <PeachButton
            onClick={handleSearchStores}
            disabled={!canProceed || !canSaveNewArea || discovering || newAreaSaving}
          >
            {newAreaSaving || discovering
              ? 'Searching…'
              : 'Search Stores'}
          </PeachButton>
          {discoverError && (
            <div style={styles.modalError}>{discoverError}</div>
          )}
          {discovering && !discoverError ? (
            <div style={styles.modalInfoText}>
              {discoveringContext === 'new'
                ? 'Looking for new stores…'
                : 'Refreshing area…'}
            </div>
          ) : null}
        </div>

        {infoMessage && <div style={styles.modalInfoText}>{infoMessage}</div>}
        {actionError && <div style={styles.modalError}>{actionError}</div>}

        {candidates.length > 0 && (
          <div style={styles.discoverList}>
            {candidates.map((candidate) => {
              const key = getCandidateKey(candidate);
              const busy = actionLoadingId === key;
              const areaDisplay =
                resolveAreaLabel(candidate.sourceAreaId) ||
                candidate.searchAreaLabel ||
                candidate.marketKey ||
                '';
              const statusDisplay = formatBusinessStatus(
                candidate.businessStatus,
                candidate.openNow
              );
              const phoneDisplay = formatPhoneNumber(candidate.phone);
              const phoneHref = candidate.phone
                ? `tel:${String(candidate.phone).replace(/[^+\d]/g, '')}`
                : '';
              const coordsDisplay =
                typeof candidate.latitude === 'number' &&
                typeof candidate.longitude === 'number'
                  ? `${candidate.latitude.toFixed(4)}, ${candidate.longitude.toFixed(4)}`
                  : '';
              const ratingParts = [];
              if (typeof candidate.rating === 'number') {
                ratingParts.push(candidate.rating.toFixed(1));
              }
              if (typeof candidate.userRatingsTotal === 'number') {
                ratingParts.push(
                  `${candidate.userRatingsTotal.toLocaleString()} reviews`
                );
              }
              const ratingDisplay = ratingParts.join(' • ');
              const priceDisplay = formatPriceLevel(candidate.priceLevel);
              const websiteDisplay = formatWebsiteHost(candidate.website);
              const websiteHref = candidate.website
                ? /^https?:/i.test(candidate.website)
                  ? candidate.website
                  : `https://${candidate.website}`
                : '';
              const primaryTypeDisplay = formatPrimaryType(
                candidate.primaryType || (candidate.types || [])[0]
              );
              const hoursLines = Array.isArray(candidate.hoursText)
                ? candidate.hoursText
                : [];
              const iconBackground =
                candidate.iconBackgroundColor || 'rgba(255,255,255,0.12)';
              const iconSrc = candidate.iconMaskBaseUri
                ? `${candidate.iconMaskBaseUri}_pinlet`
                : '';

              return (
                <div key={key} style={styles.discoverRow}>
                  <div style={styles.discoverRowHeader}>
                    <div style={styles.discoverRowIdentity}>
                      <div
                        style={{
                          ...styles.discoverIconWrap,
                          background: iconBackground,
                        }}
                      >
                        {iconSrc ? (
                          <img
                            src={iconSrc}
                            alt=""
                            style={styles.discoverIconImg}
                          />
                        ) : (
                          <span style={styles.discoverIconFallback}>🏬</span>
                        )}
                      </div>
                      <div style={styles.discoverRowIdentityText}>
                        <div style={styles.discoverRowNameLine}>
                          <span style={styles.discoverRowName}>
                            {candidate?.name || 'Store'}
                          </span>
                          <span style={styles.discoverSourceBadge}>Google</span>
                        </div>
                        <div style={styles.discoverRowIdentitySub}>
                          {areaDisplay ? `Operating Area · ${areaDisplay}` : ''}
                        </div>
                      </div>
                    </div>
                    {renderTipPill(candidate.tipEligible)}
                  </div>

                  <div style={styles.discoverRowAddress}>
                    {candidate?.formattedAddress ||
                      candidate?.address ||
                      candidate?.vicinity ||
                      'Address unavailable'}
                  </div>

                  {(primaryTypeDisplay || statusDisplay) && (
                    <div style={styles.discoverTagRow}>
                      {primaryTypeDisplay ? (
                        <span style={styles.discoverTag}>{primaryTypeDisplay}</span>
                      ) : null}
                      {statusDisplay ? (
                        <span
                          style={{
                            ...styles.discoverTag,
                            ...styles.discoverStatusTag,
                          }}
                        >
                          {statusDisplay}
                        </span>
                      ) : null}
                    </div>
                  )}

                  <div style={styles.discoverInfoGrid}>
                    {phoneDisplay ? (
                      <div style={styles.discoverInfoItem}>
                        <span style={styles.discoverInfoLabel}>Phone</span>
                        <a
                          href={phoneHref || undefined}
                          style={styles.discoverInfoLink}
                        >
                          {phoneDisplay}
                        </a>
                      </div>
                    ) : null}
                    {websiteDisplay ? (
                      <div style={styles.discoverInfoItem}>
                        <span style={styles.discoverInfoLabel}>Website</span>
                        <a
                          href={websiteHref || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.discoverInfoLink}
                        >
                          {websiteDisplay}
                        </a>
                      </div>
                    ) : null}
                    {coordsDisplay ? (
                      <div style={styles.discoverInfoItem}>
                        <span style={styles.discoverInfoLabel}>Lat / Lon</span>
                        <span style={styles.discoverInfoValue}>
                          {coordsDisplay}
                        </span>
                      </div>
                    ) : null}
                    {ratingDisplay || priceDisplay ? (
                      <div style={styles.discoverInfoItem}>
                        <span style={styles.discoverInfoLabel}>Rating</span>
                        <span style={styles.discoverInfoValue}>
                          {[ratingDisplay, priceDisplay]
                            .filter(Boolean)
                            .join(' • ')}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  {hoursLines.length > 0 ? (
                    <div style={styles.discoverHoursBox}>
                      {hoursLines.map((line, idx) => (
                        <div key={`${key}-hours-${idx}`}>{line}</div>
                      ))}
                    </div>
                  ) : null}

                  <div style={styles.discoverActions}>
                    <PeachButton
                      onClick={() => handleEnrollCandidate(candidate)}
                      disabled={!canProceed || busy}
                    >
                      {busy ? 'Working…' : 'Enroll'}
                    </PeachButton>
                    <OutlineButton
                      onClick={() => openTempExcludeFor(candidate)}
                      disabled={!canProceed || busy}
                    >
                      Temp Exclude
                    </OutlineButton>
                    <GhostButton
                      onClick={() => handleIgnoreCandidate(candidate)}
                      disabled={!canProceed || busy}
                    >
                      Not My Store
                    </GhostButton>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={styles.modalActionsRow}>
          <OutlineButton onClick={handleRequestClose}>Cancel</OutlineButton>
          <PeachButton onClick={handleRequestClose}>Done</PeachButton>
        </div>
      </div>

      {excludeState.open && (
        <div
          style={styles.modalBackdrop}
          onClick={closeExcludeModal}
        >
          <div
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <form style={styles.modalContent} onSubmit={handleSubmitExclude}>
              <div style={styles.modalHeaderRow}>
                <div style={styles.modalTitle}>Temp Exclude Store</div>
                <div style={styles.modalSubtitle}>
                  Temporarily exclude this store from enrollment.
                </div>
              </div>

              {excludeError && (
                <div style={styles.modalError}>{excludeError}</div>
              )}

              <label style={styles.modalLabel} htmlFor="exclude-reason">
                Reason
              </label>
              <select
                id="exclude-reason"
                style={styles.modalSelect}
                value={excludeState.reason}
                onChange={(e) => handleExcludeFieldChange('reason', e.target.value)}
                disabled={excludeSaving || reasonsLoading}
              >
                <option value="">
                  {reasonsLoading ? 'Loading reasons…' : 'Select a reason…'}
                </option>
                {availableReasons.map((reason) => (
                  <option key={reason.code} value={reason.code}>
                    {reason.label}
                  </option>
                ))}
              </select>

              <label style={styles.modalLabel} htmlFor="exclude-date">
                Exclude until
              </label>
              <input
                id="exclude-date"
                type="date"
                style={styles.modalInput}
                value={excludeState.date}
                min={getDefaultExcludeDate()}
                onChange={(e) => handleExcludeFieldChange('date', e.target.value)}
                disabled={excludeSaving}
              />

              <label style={styles.modalLabel} htmlFor="exclude-note">
                Notes (optional)
              </label>
              <textarea
                id="exclude-note"
                style={styles.modalTextarea}
                value={excludeState.note}
                onChange={(e) => handleExcludeFieldChange('note', e.target.value)}
                disabled={excludeSaving}
                placeholder="Add any context for your team"
              />

              <div style={styles.modalActionsRow}>
                <OutlineButton
                  type="button"
                  onClick={closeExcludeModal}
                  disabled={excludeSaving}
                >
                  Cancel
                </OutlineButton>
                <PeachButton type="submit" disabled={excludeSaving}>
                  {excludeSaving ? 'Saving…' : 'Temp Exclude'}
                </PeachButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};


/* ---------- Component ---------- */

export default function BusDash() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId: routeAccountId, brandId: brandIdParam } = useParams();

  const storedAccountId = useMemo(() => {
    try {
      return localStorage.getItem('activeAccountId');
    } catch {
      return null;
    }
  }, []);

  const accountId = routeAccountId || storedAccountId || null;

  // top-level state
  const [loading, setLoading] = useState(true);
  const [, setError] = useState('');
  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState('');

  const [showWalletHistory, setShowWalletHistory] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmNextMode, setConfirmNextMode] = useState(null);

  const [account, setAccount] = useState(null);
  const [, setBrandHeaders] = useState([]); // from /mine
  const [brandId, setBrandId] = useState(null);

  const [wallet, setWallet] = useState(null);
  const [performance, setPerformance] = useState(null);

  // allDocs: { [brandId]: fullBrandDoc }, used for rollups
  const [allDocs, setAllDocs] = useState({});
  // allBusinesses: array of banner cards across *all* docs (for multi-brand accounts)
  const [allBusinesses, setAllBusinesses] = useState([]);
  // businesses: array of banner cards for the *currently selected* brand (single-brand fallback)
  const [businesses] = useState([]);
const [summariesByBrand, setSummariesByBrand] = useState({});
const summariesByBrandRef = useRef({});
const recomputeAllBusinesses = useCallback(
  (docs) => {
    const hasDocs = docs && Object.keys(docs).length > 0;
    if (!hasDocs) {
      setAllBusinesses([]);
      return;
    }
    const cards = aggregateBannerCardsFromDocs(
      docs,
      summariesByBrandRef.current || {}
    );
    setAllBusinesses(cards);
  },
  []
);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyFilters, setHistoryFilters] = useState({
    range: '30d',
    businessId: 'ALL',
    eventType: 'ALL',
  });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExporting, setHistoryExporting] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const historyFetchMeta = useRef({ brandId: null, range: null, status: 'idle' });
  const bootstrappedRef = useRef(false);

  const { range } = historyFilters;

  const [, setAlerts] = useState([]);
  const [, setTasks] = useState([]);
  const [, setAcctStatus] = useState(null);

  const [policiesByBrand, setPoliciesByBrand] = useState({});
  const [tipPolicyData, setTipPolicyData] = useState(null);

  const [walletTs, setWalletTs] = useState('');
  const [contributionModalOpen, setContributionModalOpen] = useState(false);
  const [contributionModalCtx, setContributionModalCtx] = useState({ brandId: null, displayName: '' });
  const [contributionConfig, setContributionConfig] = useState(null);
  const [contributionForm, setContributionForm] = useState({ toPercent: '2', effectiveDate: '' });
  const [contributionLoading, setContributionLoading] = useState(false);
  const [contributionSaving, setContributionSaving] = useState(false);
  const [contributionError, setContributionError] = useState('');
  const can = useCallback(
    (role) => Array.isArray(membership?.roles) && membership.roles.includes(role),
    [membership]
  );

  const toDateInput = useCallback((value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  }, []);

  const loadContributionConfig = useCallback(async (targetBrandId) => {
    if (!targetBrandId) return;
    setContributionLoading(true);
    setContributionError('');
    try {
      const config = await apiFetch(`/dashboard/contribution-rate?brandId=${encodeURIComponent(String(targetBrandId))}`);
      setContributionConfig(config || null);
      const pending = config?.pending || null;
      const minDate = toDateInput(config?.nextEligibleAt);
      setContributionForm({
        toPercent: String(pending?.toPercent ?? config?.currentPercent ?? 2),
        effectiveDate: toDateInput(pending?.effectiveAt) || minDate,
      });
    } catch (err) {
      setContributionError(err?.message || 'Failed to load contribution settings.');
    } finally {
      setContributionLoading(false);
    }
  }, [toDateInput]);

  const openContributionModal = useCallback(async ({ brandId: targetBrandId, displayName }) => {
    if (!targetBrandId) return;
    setContributionModalCtx({ brandId: String(targetBrandId), displayName: displayName || 'Business' });
    setContributionModalOpen(true);
    await loadContributionConfig(String(targetBrandId));
  }, [loadContributionConfig]);

  const closeContributionModal = useCallback(() => {
    setContributionModalOpen(false);
    setContributionModalCtx({ brandId: null, displayName: '' });
    setContributionConfig(null);
    setContributionError('');
    setContributionSaving(false);
  }, []);

  const submitContributionChange = useCallback(async () => {
    const targetBrandId = contributionModalCtx.brandId;
    if (!targetBrandId || contributionSaving) return;
    const toPercent = Number(contributionForm.toPercent);
    const effectiveDate = String(contributionForm.effectiveDate || '').trim();
    if (!Number.isFinite(toPercent) || toPercent < 2 || toPercent > 10) {
      setContributionError('Please select a contribution rate between 2% and 10%.');
      return;
    }
    if (!effectiveDate) {
      setContributionError('Please select an effective date.');
      return;
    }

    setContributionSaving(true);
    setContributionError('');
    try {
      const effectiveAt = new Date(`${effectiveDate}T00:00:00`);
      const payload = {
        brandId: targetBrandId,
        toPercent,
        effectiveAt: effectiveAt.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      };
      if (contributionConfig?.pending?.id) {
        await apiFetch('/dashboard/contribution-rate', {
          method: 'PATCH',
          body: JSON.stringify({
            ...payload,
            scheduledChangeId: contributionConfig.pending.id,
          }),
        });
      } else {
        await apiFetch('/dashboard/contribution-rate', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      await loadContributionConfig(targetBrandId);
      setBrandRefreshCounter((v) => v + 1);
    } catch (err) {
      setContributionError(err?.message || 'Failed to save contribution change.');
    } finally {
      setContributionSaving(false);
    }
  }, [contributionModalCtx.brandId, contributionSaving, contributionForm.effectiveDate, contributionForm.toPercent, contributionConfig?.pending?.id, loadContributionConfig]);

  const cancelContributionChange = useCallback(async () => {
    const targetBrandId = contributionModalCtx.brandId;
    const pendingId = contributionConfig?.pending?.id;
    if (!targetBrandId || !pendingId || contributionSaving) return;
    setContributionSaving(true);
    setContributionError('');
    try {
      await apiFetch('/dashboard/contribution-rate', {
        method: 'DELETE',
        body: JSON.stringify({
          brandId: targetBrandId,
          scheduledChangeId: pendingId,
        }),
      });
      await loadContributionConfig(targetBrandId);
      setBrandRefreshCounter((v) => v + 1);
    } catch (err) {
      setContributionError(err?.message || 'Failed to cancel scheduled change.');
    } finally {
      setContributionSaving(false);
    }
  }, [contributionModalCtx.brandId, contributionConfig?.pending?.id, contributionSaving, loadContributionConfig]);

  useEffect(() => {
    if (!routeAccountId && accountId) {
      navigate(
        `/accounts/${encodeURIComponent(String(accountId))}/dashboard${location.search}`,
        { replace: true }
      );
    }
  }, [routeAccountId, accountId, location.search, navigate]);

  useEffect(() => {
    if (accountId) {
      setOrgContext({ orgId: String(accountId), orgType: 'account' });
    }
  }, [accountId]);

  useEffect(() => {
    if (!accountId) {
      setMembership(null);
      setMembershipError('Missing account context');
      setMembershipLoading(false);
      return;
    }

    let cancelled = false;
    setMembershipLoading(true);
    setMembershipError('');

    (async () => {
      try {
        const member = await apiFetch(
          `/org-memberships/me?orgType=account&orgId=${encodeURIComponent(String(accountId))}`,
          { orgContext: { orgId: String(accountId), orgType: 'account' } }
        );
        if (cancelled) return;
        if (member) {
          setMembership(member);
        } else {
          setMembership(null);
          setMembershipError('No membership for this account.');
        }
      } catch (err) {
        if (cancelled) return;
        setMembership(null);
        setMembershipError(err?.message || 'Unable to load membership.');
      } finally {
        if (!cancelled) setMembershipLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId]);
  const [perfTs, setPerfTs] = useState('');

  const [isSavingMode, setIsSavingMode] = useState(false);

  const [expanded, setExpanded] = useState({});

  const [brandRefreshCounter, setBrandRefreshCounter] = useState(0);

  const [driftCandidates, setDriftCandidates] = useState([]);
  const [driftLoading, setDriftLoading] = useState(false);
  const [driftError, setDriftError] = useState('');
  const [driftModalOpen, setDriftModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [excludeModalOpen, setExcludeModalOpen] = useState(false);
  const [excludeForm, setExcludeForm] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return {
      reason: '',
      date: date.toISOString().slice(0, 10),
      note: '',
    };
  });
  const [exclusionReasons, setExclusionReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [driftActionLoading, setDriftActionLoading] = useState(false);
  const [driftActionError, setDriftActionError] = useState('');

  const [addStoreOpen, setAddStoreOpen] = useState(false);
  const [addStoreHint, setAddStoreHint] = useState('');
  const [walletFundBanner, setWalletFundBanner] = useState({ tone: '', text: '' });
  const [walletFundMonitor, setWalletFundMonitor] = useState({
    open: false,
    status: 'idle',
    sessionId: '',
    startedAt: 0,
    timedOut: false,
    message: '',
  });

  useEffect(() => {
    summariesByBrandRef.current = summariesByBrand;
  }, [summariesByBrand]);

  const clearWalletFundParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('walletFundStatus');
    params.delete('session_id');
    const next = params.toString();
    navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = String(params.get('walletFundStatus') || '').toLowerCase();
    const sessionId = String(params.get('session_id') || '').trim();
    if (!statusParam) return;

    if (statusParam === 'success') {
      setWalletFundBanner({
        tone: 'success',
        text: 'Wallet funding completed.',
      });
      setBrandRefreshCounter((n) => n + 1);
      clearWalletFundParams();
      return;
    }

    if (statusParam === 'failed') {
      setWalletFundMonitor({
        open: true,
        status: 'failed',
        sessionId,
        startedAt: Date.now(),
        timedOut: true,
        message: 'Payment failed or was canceled.',
      });
      clearWalletFundParams();
      return;
    }

    setWalletFundMonitor({
      open: true,
      status: 'pending',
      sessionId,
      startedAt: Date.now(),
      timedOut: false,
      message: 'Processing your payment and wallet update…',
    });
    clearWalletFundParams();
  }, [location.search, clearWalletFundParams]);

  useEffect(() => {
    if (!walletFundMonitor.open) return;
    if (!walletFundMonitor.sessionId) return;
    if (walletFundMonitor.status === 'success') return;

    let cancelled = false;
    const startedAt = walletFundMonitor.startedAt || Date.now();

    const pollStatus = async () => {
      try {
        const data = await apiFetch(
          `/api/stripe/wallet/fund/status?session_id=${encodeURIComponent(walletFundMonitor.sessionId)}&_ts=${Date.now()}`,
          { cache: 'no-store' }
        );
        if (cancelled) return;

        const status = String(data?.status || 'pending').toLowerCase();
        if (status === 'success') {
          setWalletFundMonitor((prev) => ({
            ...prev,
            open: false,
            status: 'success',
            timedOut: false,
            message: 'Wallet funding completed.',
          }));
          setWalletFundBanner({
            tone: 'success',
            text: 'Wallet funding completed.',
          });
          setBrandRefreshCounter((n) => n + 1);
          return;
        }

        if (status === 'failed') {
          setWalletFundMonitor((prev) => ({
            ...prev,
            open: true,
            status: 'failed',
            timedOut: true,
            message: 'Payment failed or was canceled.',
          }));
          return;
        }

        const timedOut = Date.now() - startedAt >= 60000;
        setWalletFundMonitor((prev) => ({
          ...prev,
          open: true,
          status: 'pending',
          timedOut,
          message: timedOut
            ? 'Still processing. Your wallet will update shortly. You can safely close this window.'
            : 'Processing your payment and wallet update…',
        }));
      } catch (err) {
        if (cancelled) return;
        const timedOut = Date.now() - startedAt >= 60000;
        setWalletFundMonitor((prev) => ({
          ...prev,
          open: true,
          status: 'pending',
          timedOut,
          message: timedOut
            ? 'Still processing. Your wallet will update shortly. You can safely close this window.'
            : 'Verifying payment status…',
        }));
      }
    };

    pollStatus();
    const intervalId = setInterval(pollStatus, 4000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    walletFundMonitor.open,
    walletFundMonitor.sessionId,
    walletFundMonitor.startedAt,
    walletFundMonitor.status,
  ]);

  /* ---------- logout ---------- */
  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch {}
    try {
      setAuthToken(null);
      localStorage.removeItem('user');
      localStorage.removeItem('activeAccountId');
    } catch {}
    setOrgContext(null);
    navigate('/login');
  };

  /* ---------- localStorage user ---------- */
  const lsUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  /* ---------- pick initial brandId ---------- */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get('brandId');
    const fromLS = localStorage.getItem('lastBrandId');
    const initial = brandIdParam || fromQuery || fromLS || null;
    if (initial) {
      setBrandId(initial);
      localStorage.setItem('lastBrandId', initial);
    }
  }, [location.search, brandIdParam]);

  useEffect(() => {
    if (brandId) setAddStoreHint('');
  }, [brandId, accountId, membership]);

  // Refresh /mine after add/updates
  useEffect(() => {
    if (!accountId || membershipLoading || !membership) return;
    // Allow bootstrap effect to handle the very first load
    if (!bootstrappedRef.current && brandRefreshCounter === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const j = await apiFetch('/mine');
        const mine = Array.isArray(j?.brands) ? j.brands : [];
        if (cancelled) return;
        setBrandHeaders(mine);
        setAllDocs((prev) => {
          const merged = { ...prev };
          mine.forEach((brand) => {
            const id = normalizeBrandKey(brand?._id || brand?.brandId);
            if (!id) return;
            merged[id] = { ...(merged[id] || {}), ...brand };
          });
          recomputeAllBusinesses(merged);
          return merged;
        });
      } catch (err) {
        console.warn('Failed to refresh /mine:', err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, membershipLoading, membership, brandRefreshCounter, recomputeAllBusinesses]);

  /* ---------- boot: load account + /mine and, if multi, preload all docs ---------- */
  useEffect(() => {
    if (bootstrappedRef.current) return;
    if (!accountId || membershipLoading || !membership) return;
    bootstrappedRef.current = true;
    let mounted = true;
    (async () => {
      try {
        setError('');

        // /auth/me gives us account context
        let me = null;
        try {
          me = await apiFetch('/auth/me');
        } catch (err) {
          console.warn(
            'Failed to load account-scoped brands from /dashboard/summary/account:',
            err?.message || err
          );
        }
        if (mounted) setAccount(me);

        // /mine gives us direct member brands; account summary + /auth/me brands
        // provide full account EIN footprint for cold starts.
        let mine = [];
        let accountSummaryBrands = [];
        let authMeBrands = [];
        try {
          const j = await apiFetch('/mine');
          mine = Array.isArray(j?.brands) ? j.brands : [];
        } catch {}
        try {
          authMeBrands = Array.isArray(me?.brands) ? me.brands : [];
        } catch {}
        try {
          const acct = await apiFetch(
            `/dashboard/summary/account?orgId=${encodeURIComponent(
              String(accountId)
            )}`
          );
          accountSummaryBrands = Array.isArray(acct?.brands) ? acct.brands : [];
        } catch {}

        const mergedHeadersById = new Map();
        [...mine, ...accountSummaryBrands, ...authMeBrands].forEach((b) => {
          const id = normalizeBrandKey(b?._id || b?.id || b?.brandId);
          if (!id) return;
          const existing = mergedHeadersById.get(id) || {};
          mergedHeadersById.set(id, { ...existing, ...b, _id: id });
        });
        const preloadHeaders = Array.from(mergedHeadersById.values());
        console.info('[BusDash] brand source counts', {
          mine: mine.length,
          accountSummary: accountSummaryBrands.length,
          authMeBrands: authMeBrands.length,
          merged: preloadHeaders.length,
          mergedIds: preloadHeaders.map((b) =>
            normalizeBrandKey(b?._id || b?.id || b?.brandId)
          ),
        });
        if (mounted) setBrandHeaders(preloadHeaders);

        // Pre-fetch brand docs so we can build account-level banner cards.
        if (mounted && Array.isArray(preloadHeaders) && preloadHeaders.length > 0) {
          try {
            const pairs = await Promise.all(
              preloadHeaders.map(async (b) => {
                const id = normalizeBrandKey(b?._id || b?.id || b?.brandId);
                if (!id) return null;
                try {
                  const [doc, summary, policy] = await Promise.all([
                    apiFetch(`/brand/${encodeURIComponent(id)}`),
                    apiFetch(
                      `/dashboard/summary?brandId=${encodeURIComponent(id)}`
                    ).catch(() => null),
                    fetchWalletPolicy(apiFetch, id).catch(() => null),
                  ]);
                  return { id, doc, summary, policy, header: b };
                } catch {
                  return { id, doc: null, summary: null, header: b };
                }
              })
            );

            // Seed with merged headers so account footprint is preserved even if
            // one of the per-brand fetches fails or returns partial data.
            const map = {};
            preloadHeaders.forEach((header) => {
              const hid = normalizeBrandKey(
                header?._id || header?.id || header?.brandId
              );
              if (!hid) return;
              map[hid] = header;
            });
            const summaryMap = {};
            const policyMap = {};
            pairs.forEach((entry) => {
              if (!entry || !entry.id) return;
              const doc = entry.doc || entry.header;
              if (doc) {
                map[entry.id] = doc;
              }
              if (entry.summary) {
                summaryMap[entry.id] = entry.summary;
              }
              if (entry.policy?.totals || entry.policy?.perStore) {
                policyMap[entry.id] = entry.policy;
              }
            });

            if (mounted) {
              setAllDocs(() => {
                recomputeAllBusinesses(map);
                return map;
              });
              if (Object.keys(summaryMap).length) {
                setSummariesByBrand((prev) => ({ ...prev, ...summaryMap }));
              }
              if (Object.keys(policyMap).length) {
                setPoliciesByBrand((prev) => ({ ...prev, ...policyMap }));
              }

              // build banner cards across ALL brands
              const cards = aggregateBannerCardsFromDocs(map, summaryMap);
              setAllBusinesses(cards);
            }
          } catch {
            // ignore preload errors
          }
        }

        if (!mounted) return;

        // pick which brand to "focus" dashboard on
        const firstBrandId =
          (Array.isArray(preloadHeaders) ? preloadHeaders : [])
            .map((b) => normalizeBrandKey(b?._id || b?.id || b?.brandId))
            .find((id) => typeof id === 'string' && id.length > 0) || null;

        let chosen =
          normalizeBrandKey(brandId) ||
          normalizeBrandKey(brandIdParam) ||
          normalizeBrandKey(new URLSearchParams(location.search).get('brandId')) ||
          normalizeBrandKey(me?.primaryBrand?._id) ||
          normalizeBrandKey(me?.primaryBrand) ||
          normalizeBrandKey(preloadHeaders[0]?._id || preloadHeaders[0]?.id || preloadHeaders[0]?.brandId) ||
          normalizeBrandKey(localStorage.getItem('lastBrandId')) ||
          null;

        const validBrandIds = new Set(
          (Array.isArray(preloadHeaders) ? preloadHeaders : [])
            .map((b) => normalizeBrandKey(b?._id || b?.id || b?.brandId))
            .filter(Boolean)
        );

        if (!chosen || chosen === '__ALL__' || !validBrandIds.has(chosen)) {
          chosen = firstBrandId || null;
        }

        if (chosen) {
          setBrandId(chosen);
          localStorage.setItem('lastBrandId', chosen);
          if (brandIdParam !== chosen) {
            const nextUrl = `/accounts/${encodeURIComponent(
              String(accountId)
            )}/dashboard?brandId=${encodeURIComponent(String(chosen))}`;
            navigate(nextUrl, { replace: true });
          }
        } else {
          setBrandId(null);
        }
      } catch {
        if (mounted) setError('Failed to load account/brands.');
      }
    })();
    return () => {
      mounted = false;
    };
  }, [accountId, membershipLoading, membership, brandId, brandIdParam, location.search, navigate, recomputeAllBusinesses]);

  /* ---------- fetch selected brand dashboard data ---------- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!brandId || !accountId || !membership) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        setError('');

        // Full brand doc
        let brandDoc = null;
        try {
          brandDoc = await apiFetch(`/brand/${encodeURIComponent(brandId)}`);
        } catch {
          brandDoc = null;
        }
        if (!mounted) return;

        // Dashboard summary
        let dash = null;
        let accountDash = null;
        try {
          const qs = `?brandId=${encodeURIComponent(brandId)}`;
          [dash, accountDash] = await Promise.all([
            apiFetch(`/dashboard/summary${qs}`),
            apiFetch(
              `/dashboard/summary/account?orgId=${encodeURIComponent(
                String(accountId)
              )}`
            ).catch(() => null),
          ]);
        } catch {
          dash = null;
          accountDash = null;
        }
        if (!mounted) return;

        // Merge this doc into allDocs (so single-brand accounts still have allDocs populated)
        if (mounted) {
          setAllDocs((prev) => {
            const next = {
              ...prev,
              [brandId]: brandDoc || {},
            };
            recomputeAllBusinesses(next);
            return next;
          });
        }

        // Wallet data
        if (mounted) {
          const dashWallet = dash?.wallet || {};
          const accountWallet = accountDash?.wallet || {};
          const accountPerf = accountDash?.performance || {};
          setWallet(
            {
              ...dashWallet,
              balanceCents: Number(dashWallet.balanceCents || 0),
              paymentType:
                dashWallet.paymentType ||
                (brandDoc?.topUpConfig?.mode === 'manual' ? 'manual' : 'auto'),
              minBalanceCents: Number(
                dashWallet.minBalanceCents ?? 0
              ),
              replenishAmountCents: Number(
                dashWallet.replenishAmountCents ?? 0
              ),
              lastTopupAt: dashWallet.lastTopupAt || null,
              ...(accountWallet?.balanceCents !== undefined ? { balanceCents: Number(accountWallet.balanceCents || 0) } : {}),
              ...(accountWallet?.fundedToDateCents !== undefined ? { fundedToDateCents: Number(accountWallet.fundedToDateCents || 0) } : {}),
              ...(accountPerf?.contributions?.lifetimeCents !== undefined ? { spentOnContributionsCents: Number(accountPerf.contributions.lifetimeCents || 0) } : {}),
              ...(accountPerf?.fees?.lifetimeCents !== undefined ? { spentOnFeesCents: Number(accountPerf.fees.lifetimeCents || 0) } : {}),
            }
          );
          setWalletTs(accountDash?.walletTimestamp || dash?.walletTimestamp || '');
        }

        // Perf data
        if (mounted) {
          setPerformance(
            accountDash?.performance || dash?.performance || {
              contributions: { lifetimeCents: 0, last30dCents: 0 },
              fees: { lifetimeCents: 0, last30dCents: 0 },
              transactions: { lifetime: 0, last30d: 0 },
              channelMix: { pos30d: 0, onlineZip30d: 0 },
            }
          );
          setPerfTs(accountDash?.performanceTimestamp || dash?.performanceTimestamp || '');
        }

        if (mounted && dash) {
          setSummariesByBrand((prev) => ({ ...prev, [brandId]: dash }));
        }

        // markets no longer persisted for modal suggestions

        // alerts / tasks
        if (mounted) {
          const foundAlerts = Array.isArray(dash?.alerts) ? dash.alerts : [];
          const foundTasks = Array.isArray(dash?.tasks) ? dash.tasks : [];
          setAlerts(foundAlerts);
          setTasks(foundTasks);
          setAcctStatus(dash?.accountStatus ?? null);

          // "hidden from consumer" UX nudge
          if (dash?.hiddenFromConsumer) {
            setAlerts((prev) => {
              const existing = Array.isArray(prev) ? prev : [];
              if (existing.some((a) => a.id === 'a_hidden_consumer'))
                return existing;
              return [
                ...existing,
                {
                  id: 'a_hidden_consumer',
                  type: 'visibility',
                  severity: 'medium',
                  message:
                    'Hidden from consumer app until balance recovers',
                },
              ];
            });
          } else {
            setAlerts((prev) =>
              (prev || []).filter((a) => a.id !== 'a_hidden_consumer')
            );
          }
        }

        // policy (wallet thresholds, etc)
        let policyResp = null;
        try {
          policyResp = await fetchWalletPolicy(apiFetch, brandId);
        } catch {
          policyResp = null;
        }

        if (mounted) {
          const nextPolicy = policyResp && policyResp.perStore
            ? policyResp
            : (() => {
                const perStore = pickFallbackPolicy(brandDoc?.category);
                const storesActive = Array.isArray(brandDoc?.stores)
                  ? brandDoc.stores.filter(isStoreActive).length
                  : 0;
                return {
                  category: brandDoc?.category || 'Specialty Retail',
                  storesActive,
                  perStore,
                  totals: {
                    minTopUpCents: storesActive * perStore.minTopUpCents,
                    reloadCents: storesActive * perStore.reloadCents,
                    autoThresholdCents:
                      storesActive * perStore.autoThresholdCents,
                    manualThresholdCents:
                      storesActive * perStore.manualThresholdCents,
                  },
                };
              })();
          setPoliciesByBrand((prev) => ({
            ...prev,
            [brandId]: nextPolicy,
          }));
        }

        // Tip policy (used for per-location tip eligibility highlighting)
        try {
          const tp = await apiFetch(
            `/brand/tip-policy?brandId=${encodeURIComponent(brandId)}`
          );
          if (mounted) setTipPolicyData(tp);
        } catch {
          if (mounted) setTipPolicyData(null);
        }

        // Recompute cards from full account docs; do not collapse to one selected brand.
        if (mounted) {
          setAllDocs((prev) => {
            const next = {
              ...(prev || {}),
              [brandId]: brandDoc || {},
            };
            recomputeAllBusinesses(next);
            return next;
          });
        }
      } catch {
        if (mounted) setError('Failed to load dashboard.');
      } finally {
        mounted && setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [brandId, accountId, membership, brandRefreshCounter, recomputeAllBusinesses]);

  /* ---------- drift suggestions ---------- */
  useEffect(() => {
    let cancelled = false;
    if (!brandId) {
      setDriftCandidates([]);
      setDriftError('');
      setDriftLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setDriftLoading(true);
    setDriftError('');

    (async () => {
      const paths = [
        `/brand/locations/suggested?brandId=${encodeURIComponent(brandId)}`,
        `/brand/drift?brandId=${encodeURIComponent(brandId)}`,
      ];
      for (let i = 0; i < paths.length; i += 1) {
        try {
          const resp = await apiFetch(paths[i]);
          if (!cancelled) {
            setDriftCandidates(Array.isArray(resp) ? resp : []);
            setDriftLoading(false);
          }
          return;
        } catch (err) {
          if (i === paths.length - 1 && !cancelled) {
            setDriftError(err.message || 'Unable to load suggestions');
            setDriftCandidates([]);
            setDriftLoading(false);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [brandId, brandRefreshCounter]);

  /* ---------- actions ---------- */
  const handleAddBusiness = () => {
    if (!can('org_admin')) return;
    const url = accountId
      ? `/accounts/${encodeURIComponent(
          String(accountId)
        )}/registration?mode=add&step=2&source=dash#step=2`
      : '/business-registration?mode=add&step=2&source=dash#step=2';

    try {
      sessionStorage.setItem('reg.startStep', '2');
    } catch {}

    try {
      localStorage.removeItem('registrationSessionId');
      Object.keys(localStorage)
        .filter((key) => key.startsWith('registrationDraft:'))
        .forEach((key) => localStorage.removeItem(key));
    } catch {}

    navigate(url, {
      replace: false,
      state: { step: 2, source: 'dash', mode: 'add' },
    });
  };

  const openWalletHistory = () => {
    setHistoryError('');
    setShowWalletHistory(true);
  };
  const closeWalletHistory = () => setShowWalletHistory(false);
  const handleExportWalletHistory = useCallback(async () => {
    if (!brandId || historyExporting) return;
    setHistoryError('');
    setHistoryExporting(true);
    try {
      const token = getAuthToken();
      const orgContext = getOrgContext() || (accountId
        ? { orgId: String(accountId), orgType: 'account' }
        : null);
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (orgContext?.orgId) {
        headers['X-Org-Id'] = String(orgContext.orgId);
        headers['X-Org-Type'] = String(orgContext.orgType || 'account');
      }

      const url = `${API_PREFIX}/wallet/history/export?brandId=${encodeURIComponent(
        brandId
      )}&range=${encodeURIComponent(historyFilters.range)}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers,
      });

      if (!response.ok) {
        const fallback = 'Unable to export wallet history right now.';
        let msg = fallback;
        try {
          const data = await response.json();
          msg = data?.error || data?.message || fallback;
        } catch {
          try {
            const text = await response.text();
            msg = (text || '').slice(0, 200) || fallback;
          } catch {
            msg = fallback;
          }
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const cd = response.headers.get('content-disposition') || '';
      const match =
        cd.match(/filename\*=UTF-8''([^;]+)/i) ||
        cd.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1]
        ? decodeURIComponent(match[1])
        : `wallet-history-${new Date().toISOString().slice(0, 10)}.csv`;

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Failed to export wallet history', err);
      setHistoryError(err?.message || 'Unable to export wallet history right now.');
    } finally {
      setHistoryExporting(false);
    }
  }, [accountId, brandId, historyExporting, historyFilters.range]);

  const handleFundWallet = async () => {
    try {
      const currentUrl = window.location.href;
      const response = await apiFetch('/api/brand/wallet/fund', {
        method: 'POST',
        body: JSON.stringify({
          brandId,
          currentUrl: currentUrl,
        }),
      });

      // 2️⃣ If your apiFetch auto-parses JSON:
      if (!response?.url) {
        throw new Error(response?.error || 'Failed to create checkout session');
      }

      // 3️⃣ Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (err) {
      console.error('Fund wallet failed:', err);
      alert('Unable to start checkout. Please try again.');
    }
  };

  const requestToggleMode = (nextMode) => {
    const desired = nextMode === 'manual' ? 'manual' : 'auto';
    const current = wallet?.paymentType === 'manual' ? 'manual' : 'auto';
    if (desired === current) return;
    setConfirmNextMode(desired);
    setConfirmOpen(true);
  };

  const handleToggleMode = async (desired) => {
    if (!brandId || isSavingMode) return;
    const current = wallet?.paymentType === 'manual' ? 'manual' : 'auto';
    if (desired === current) return;

    setIsSavingMode(true);
    const prevWallet = wallet;

    try {
      await apiFetch('/brand/wallet/configure', {
        method: 'PUT',
        body: JSON.stringify({ brandId, mode: desired }),
      });

      // optimistic update
      setWallet((w) => ({ ...(w || {}), paymentType: desired }));
      setAllDocs((prev) => {
        const current = prev[brandId] || {};
        const next = {
          ...prev,
          [brandId]: {
            ...current,
            topUpConfig: {
              ...(current.topUpConfig || {}),
              mode: desired,
            },
          },
        };
        recomputeAllBusinesses(next);
        return next;
      });

      // re-fetch wallet policy for new mode
      try {
        const newPolicy = await apiFetch(
          `/brand/wallet/policy?brandId=${encodeURIComponent(brandId)}`
        );
        setPoliciesByBrand((prev) => ({
          ...prev,
          [brandId]: newPolicy,
        }));
      } catch {}

      // refresh acct status + alerts
      try {
        const s = await apiFetch(
          `/dashboard/summary?brandId=${encodeURIComponent(brandId)}`
        );
        setAcctStatus(s?.accountStatus ?? null);

        if (s?.hiddenFromConsumer) {
          setAlerts((prev) => {
            const existing = Array.isArray(prev) ? prev : [];
            if (existing.some((a) => a.id === 'a_hidden_consumer'))
              return existing;
            return [
              ...existing,
              {
                id: 'a_hidden_consumer',
                type: 'visibility',
                severity: 'medium',
                message:
                  'Hidden from consumer app until balance recovers',
              },
            ];
          });
        } else {
          setAlerts((prev) =>
            (prev || []).filter(
              (a) => a.id !== 'a_hidden_consumer'
            )
          );
        }
      } catch {}

      // refresh tip policy too
      try {
        const tp = await apiFetch(
          `/brand/tip-policy?brandId=${encodeURIComponent(brandId)}`
        );
        setTipPolicyData(tp);
      } catch {}
  } catch (e) {
    // rollback
    setWallet(prevWallet);
    console.error('Toggle mode error:', e);
    alert('Could not update payment mode. Please try again.');
  } finally {
    setIsSavingMode(false);
    setConfirmOpen(false);
    setConfirmNextMode(null);
  }
};

  const refreshBrandData = () => setBrandRefreshCounter((prev) => prev + 1);

  const handleOpenAddStore = () => {
    if (!brandId) {
      setAddStoreHint('Select a brand to add stores.');
      return;
    }
    setAddStoreHint('');
    setAddStoreOpen(true);
  };

  const handleCloseAddStore = () => {
    setAddStoreOpen(false);
  };

  const getCandidateKey = (candidate) =>
    [
      candidate?._id,
      candidate?.placeId,
      candidate?.googlePlaceId,
      candidate?.locationId,
      candidate?.name,
      candidate?.formattedAddress,
    ]
      .map((v) => (v !== undefined && v !== null ? String(v) : ''))
      .find((v) => v.length > 0) || '';

  const removeCandidateFromList = (candidate) => {
    setDriftCandidates((prev) => {
      const key = getCandidateKey(candidate);
      if (key) {
        return prev.filter((item) => getCandidateKey(item) !== key);
      }
      return prev.filter((item) => item !== candidate);
    });
  };

  const handleOpenSuggestionModal = (candidate) => {
    setSelectedCandidate(candidate);
    setDriftActionError('');
    setExcludeModalOpen(false);
    resetExcludeForm();
    setDriftModalOpen(true);
  };

  const resetExcludeForm = () => {
    const next = new Date();
    next.setDate(next.getDate() + 30);
    setExcludeForm({
      reason: '',
      date: next.toISOString().slice(0, 10),
      note: '',
    });
  };

  const handleCloseSuggestionModal = () => {
    setDriftModalOpen(false);
    setSelectedCandidate(null);
    setDriftActionError('');
    setDriftActionLoading(false);
    setExcludeModalOpen(false);
    resetExcludeForm();
  };

  const ensureExclusionReasons = async () => {
    if (exclusionReasons.length || reasonsLoading) return;
    setReasonsLoading(true);
    try {
      const resp = await apiFetch(
        `/brand/exclusions/reasons?brandId=${encodeURIComponent(brandId || '')}`
      );
      if (Array.isArray(resp?.reasons) && resp.reasons.length) {
        setExclusionReasons(resp.reasons);
      } else {
        setExclusionReasons(FALLBACK_EXCLUSION_REASONS);
      }
    } catch {
      setExclusionReasons(FALLBACK_EXCLUSION_REASONS);
    } finally {
      setReasonsLoading(false);
    }
  };

  const handleOpenTempExcludeModal = async () => {
    if (!selectedCandidate) return;
    resetExcludeForm();
    setDriftActionError('');
    setExcludeModalOpen(true);
    await ensureExclusionReasons();
  };

  const handleExcludeFieldChange = (field, value) => {
    setExcludeForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTempExcludeSubmit = async (event) => {
    event?.preventDefault?.();
    if (!selectedCandidate || !brandId) return;

    if (!excludeForm.reason) {
      setDriftActionError('Select a reason for the exclusion.');
      return;
    }

    const date = new Date(excludeForm.date);
    if (!excludeForm.date || Number.isNaN(date.getTime()) || date <= new Date()) {
      setDriftActionError('Exclude-until must be a future date.');
      return;
    }

    setDriftActionLoading(true);
    setDriftActionError('');
    try {
      await apiFetch('/brand/location/temp-exclude', {
        method: 'POST',
        body: JSON.stringify({
          brandId,
          suggestionId: selectedCandidate._id,
          placeId: selectedCandidate.placeId,
          formattedAddress: selectedCandidate.formattedAddress,
          name: selectedCandidate.name,
          marketKey: selectedCandidate.marketKey,
          reason: excludeForm.reason,
          excludeUntil: date.toISOString(),
          notes: excludeForm.note ? excludeForm.note.trim() : undefined,
        }),
      });
      refreshBrandData();
      removeCandidateFromList(selectedCandidate);
      handleCloseSuggestionModal();
    } catch (err) {
      setDriftActionError(err.message || 'Failed to temp-exclude this location.');
    } finally {
      setDriftActionLoading(false);
    }
  };

  const goToStep3ForCandidate = (candidate) => {
    const params = new URLSearchParams();
    params.set('mode', 'add_location');
    params.set('step', '3');
    const url = `/business-registration?${params.toString()}#step=3`;
    try {
      sessionStorage.setItem('reg.startStep', '3');
    } catch {}
    navigate(url, {
      replace: false,
      state: { candidate },
    });
  };

  const handleEnrollSuggestion = async () => {
    if (!selectedCandidate || !brandId) return;
    if (!selectedCandidate._id) {
      handleCloseSuggestionModal();
      goToStep3ForCandidate(selectedCandidate);
      return;
    }

    setDriftActionLoading(true);
    setDriftActionError('');
    try {
      await apiFetch('/brand/location/adopt', {
        method: 'POST',
        body: JSON.stringify({
          brandId,
          suggestionId: selectedCandidate._id,
        }),
      });
      refreshBrandData();
      removeCandidateFromList(selectedCandidate);
      handleCloseSuggestionModal();
    } catch (err) {
      setDriftActionError(err.message || 'Failed to enroll this location.');
    } finally {
      setDriftActionLoading(false);
    }
  };

  const handleIgnoreSuggestion = async () => {
    if (!selectedCandidate || !brandId) return;
    if (!selectedCandidate._id) {
      removeCandidateFromList(selectedCandidate);
      handleCloseSuggestionModal();
      return;
    }

    setDriftActionLoading(true);
    setDriftActionError('');
    try {
      await apiFetch('/brand/drift/ignore', {
        method: 'POST',
        body: JSON.stringify({
          brandId,
          suggestionId: selectedCandidate._id,
        }),
      });
      removeCandidateFromList(selectedCandidate);
      handleCloseSuggestionModal();
    } catch (err) {
      setDriftActionError(err.message || 'Failed to ignore this suggestion.');
    } finally {
      setDriftActionLoading(false);
    }
  };

  const handleSnoozeSuggestion = async (days = 30) => {
    if (!selectedCandidate || !brandId) return;
    if (!selectedCandidate._id) {
      handleCloseSuggestionModal();
      return;
    }

    setDriftActionLoading(true);
    setDriftActionError('');
    try {
      await apiFetch('/brand/locations/snooze', {
        method: 'POST',
        body: JSON.stringify({
          brandId,
          suggestionId: selectedCandidate._id,
          days,
        }),
      });
      removeCandidateFromList(selectedCandidate);
      handleCloseSuggestionModal();
    } catch (err) {
      setDriftActionError(err.message || 'Failed to snooze this suggestion.');
    } finally {
      setDriftActionLoading(false);
    }
  };

  /* ---------- Derived view data ---------- */

  // Friendly account owner name
  const ownerName = useMemo(() => {
    const fromAccount =
      [account?.firstName, account?.lastName].filter(Boolean).join(' ') ||
      account?.email;
    const fromLS =
      lsUser?.fullName ||
      [lsUser?.firstName, lsUser?.lastName].filter(Boolean).join(' ') ||
      lsUser?.name ||
      lsUser?.email;
    return fromAccount || fromLS || 'Account';
  }, [account, lsUser]);

  const lastLoginAt =
    account?.lastLoginAt ||
    account?.lastSeenAt ||
    lsUser?.lastLoginAt ||
    lsUser?.lastSeenAt ||
    null;
  const lastLoginDisplay = lastLoginAt
    ? new Date(lastLoginAt).toLocaleString()
    : '—';

  // BRAND policy (as fetched / fallback within brand scope)
  const policy = brandId ? policiesByBrand[brandId] : null;
  const brandPolicyTotals = policy?.totals || null;

  // ACCOUNT policy derived from all active locations across ALL brands
  const accountPolicyFromMix = useMemo(() => {
    const policyTotals = {
      minTopUpCents: 0,
      initialTopUpCents: 0,
      reloadCents: 0,
      autoThresholdCents: 0,
      manualThresholdCents: 0,
    };
    let policyStoresActive = 0;
    let policyCount = 0;

    Object.entries(allDocs || {}).forEach(([docKey, doc]) => {
      const key = normalizeBrandKey(docKey) || normalizeBrandKey(doc?._id || doc?.id || doc?.brandId);
      const policyForBrand = key ? policiesByBrand[key] : null;
      const totals = policyForBrand?.totals;
      if (!totals) return;
      policyCount += 1;
      policyStoresActive += Number(policyForBrand?.storesActive || totals.activeStores || 0);
      policyTotals.minTopUpCents += Number(totals.minTopUpCents || totals.initialTopUpCents || 0);
      policyTotals.initialTopUpCents += Number(totals.initialTopUpCents || totals.minTopUpCents || 0);
      policyTotals.reloadCents += Number(totals.reloadCents || 0);
      policyTotals.autoThresholdCents += Number(totals.autoThresholdCents || 0);
      policyTotals.manualThresholdCents += Number(totals.manualThresholdCents || 0);
    });

    if (policyCount > 0) {
      return { storesActive: policyStoresActive, totals: policyTotals };
    }

    const mix = summarizeActiveByCategory(allDocs);
    return computeTotalsFromMix(mix); // { storesActive, totals }
  }, [allDocs, policiesByBrand]);

  // Choose which totals to display (account-level preferred for multi-brand or larger requirement)
  const FORCE_BRAND_SCOPE = false;
  const effectiveTotals = useMemo(() => {
    if (FORCE_BRAND_SCOPE) return brandPolicyTotals || accountPolicyFromMix?.totals || null;

    const multi = (allBusinesses?.length || 0) > 1;
    const acctTotals = accountPolicyFromMix?.totals || null;

    if (multi) return acctTotals || brandPolicyTotals;

    // Single brand, but account view might still have more accurate count
    const brandMin = brandPolicyTotals?.minTopUpCents || 0;
    const acctMin  = acctTotals?.minTopUpCents || 0;
    if (acctMin > brandMin) return acctTotals;

    return brandPolicyTotals || acctTotals || null;
  }, [FORCE_BRAND_SCOPE, allBusinesses, brandPolicyTotals, accountPolicyFromMix]);

  // Top-up threshold uses the chosen totals
  const thresholdDisplayCents = useMemo(() => {
    if (!effectiveTotals) return 0;
    const manual = wallet?.paymentType === 'manual';
    return manual
      ? effectiveTotals.manualThresholdCents
      : effectiveTotals.autoThresholdCents;
  }, [effectiveTotals, wallet?.paymentType]);

  const paymentTypeText =
    wallet?.paymentType === 'manual' ? 'Manual (1.75%)' : 'Auto (1.00%)';

  // Which stores are eligible for tips (for per-location badge)
  const eligibleStores = useMemo(
    () =>
      Array.isArray(tipPolicyData?.effectiveStores)
        ? tipPolicyData.effectiveStores.filter(
            (s) => s && (s.eligible === true || s.percent > 0)
          )
        : [],
    [tipPolicyData]
  );

  const tipEligibleKeySet = useMemo(() => {
    const set = new Set();
    eligibleStores.forEach((store) => {
      [
        store?.storeId,
        store?.id,
        store?.locationId,
        store?.placeId,
        store?._id,
        store?.location?.id,
      ]
        .map((k) => (k !== undefined && k !== null ? String(k) : ''))
        .filter(Boolean)
        .forEach((key) => set.add(key));
    });
    return set;
  }, [eligibleStores]);

  // Which set of cards should we render?
  const businessesToShow = useMemo(() => {
    if (allBusinesses.length) return allBusinesses;
    return businesses;
  }, [allBusinesses, businesses]);

  /**
   * Build BusinessSectionData:
   * - For each banner card, pull its locations from allDocs (across all brandIds behind that card)
   * - Compute coverage summary and tipEligible
   */
  const businessSectionData = useMemo(() => {
    const items = businessesToShow.map((card) => {
      // build aggregated locations
      const locations = buildLocationsForBannerCard(
        card,
        allDocs,
        tipEligibleKeySet
      );

      // coverage counts
      const expectedFromCard = card.coverageExpected || 0;
      const enrolledFromCard = card.coverageEnrolled || 0;

      // derive from locations for safety
      const fromLocExpected = locations.length;
      const fromLocActive = locations.filter(
        (l) => l.statusCode === 'active'
      ).length;

      const expected = Math.max(
        expectedFromCard,
        fromLocExpected,
        enrolledFromCard
      );
      const active = Math.max(enrolledFromCard, fromLocActive);

      // registration date display
      const regDateDisplay = card.regDateRaw
        ? new Date(card.regDateRaw).toLocaleDateString()
        : '—';

      const hasContributionData = card?.contributions?.hasData === true;
      const lifetimeCents = Number(card?.contributions?.lifetimeCents || 0);
      const last30dCents = Number(card?.contributions?.last30dCents || 0);
      const baseContributionDisplay = (() => {
        const values = Array.isArray(card?.baseContributionPercents)
          ? card.baseContributionPercents
              .map((n) => Number(n))
              .filter((n) => Number.isFinite(n))
          : [];
        if (!values.length) return '—';
        const uniques = Array.from(new Set(values)).sort((a, b) => a - b);
        const formatted = uniques.map((n) =>
          `${(Math.round(n * 100) / 100).toString().replace(/\.0+$/, '')}%`
        );
        return formatted.join(' • ');
      })();

      return {
        id: card.id,
        brandIds: Array.isArray(card.brandIds) ? card.brandIds : [],
        displayName: card.displayName,
        einTaxIdDisplay: card.einTaxIdDisplay, // may be "11-2345678 • 22-5478561" or "Unassigned"
        category: card.category || 'Uncategorized',
        lifecycleStatusLabel: titleCase(
          card.lifecycleStatusLabel || 'Onboarding'
        ),
        regDateDisplay,
        coverageSummary: `${active}/${Math.max(expected, 1)}`,
        logoUrl: card.logoUrl || DEFAULT_BRAND_ICON,
        tipEligible:
          card.tipEligible ||
          locations.some((l) => l.tipEligible === true),
        locations,
        totalLocations: expected,
        activeLocations: active,
        baseContributionDisplay,
        contribLast30Display: hasContributionData
          ? centsToDollar(last30dCents)
          : '—',
        contribLifetimeDisplay: hasContributionData
          ? centsToDollar(lifetimeCents)
          : '—',
        contribLast30Cents: last30dCents,
        contribLifetimeCents: lifetimeCents,
      };
    });

    // section header totals
    const totals = items.reduce(
      (acc, item) => {
        acc.total += item.totalLocations || 0;
        acc.active += item.activeLocations || 0;
        return acc;
      },
      { total: 0, active: 0 }
    );

    return {
      totalLocations: totals.total,
      activeLocations: totals.active,
      items,
    };
  }, [businessesToShow, allDocs, tipEligibleKeySet]);

  useEffect(() => {
    console.info('[BusDash] allBusinesses', {
      count: allBusinesses.length,
      ids: allBusinesses.map((b) => b.id),
      names: allBusinesses.map((b) => b.displayName),
      eins: allBusinesses.map((b) => b.einTaxIdDisplay),
    });
  }, [allBusinesses]);

  useEffect(() => {
    const items = businessSectionData?.items || [];
    console.info('[BusDash] businessSectionData', {
      count: items.length,
      ids: items.map((b) => b.id),
      names: items.map((b) => b.displayName),
      eins: items.map((b) => b.einTaxIdDisplay),
    });
  }, [businessSectionData]);

  const addStoreEinOptions = useMemo(() => {
    const map = new Map();

    Object.entries(allDocs || {}).forEach(([docId, doc]) => {
      if (!doc) return;

      const einDigits = normalizeEin(readAnyEin(doc));
      const entryKey = einDigits ? `ein:${einDigits}` : 'ein:unassigned';
      const label = einDigits ? formatEin(einDigits) : 'Unassigned';

      if (!map.has(entryKey)) {
        map.set(entryKey, {
          key: entryKey,
          label,
          brands: [],
        });
      }

      const entry = map.get(entryKey);
      const existingSet = new Set(
        entry.brands.map((brand) => `${brand.brandId}::${brand.label}`)
      );

      const bannerNames = getBannerNames(doc);
      const fallbackName = doc?.name || doc?.legalName || label;
      const names = bannerNames.length ? bannerNames : [fallbackName];

      names.forEach((brandName, idx) => {
        const safeName = brandName || fallbackName || `Brand ${idx + 1}`;
        const identifier = `${docId}::${safeName}`;
        if (existingSet.has(identifier)) return;
        existingSet.add(identifier);
        entry.brands.push({
          key: `${docId}::${idx}`,
          label: safeName,
          brandId: docId,
        });
      });
    });

    const options = Array.from(map.values());
    options.sort((a, b) => a.label.localeCompare(b.label));
    options.forEach((option) =>
      option.brands.sort((a, b) => a.label.localeCompare(b.label))
    );
    return options;
  }, [allDocs]);

  const businessOptions = useMemo(
    () => businessSectionData.items || [],
    [businessSectionData.items]
  );

  const businessMapByName = useMemo(() => {
    const map = new Map();
    businessOptions.forEach((item) => {
      map.set((item.displayName || '').toLowerCase().trim(), item.id);
    });
    return map;
  }, [businessOptions]);

  const businessIdSet = useMemo(() => {
    const set = new Set();
    businessOptions.forEach((item) => set.add(item.id));
    return set;
  }, [businessOptions]);

  useEffect(() => {
    if (!showWalletHistory || !brandId) return;

    const meta = historyFetchMeta.current;
    if (
      meta.brandId === brandId &&
      meta.range === range &&
      (historyRows.length > 0 ||
        meta.status === 'success' ||
        meta.status === 'error' ||
        meta.status === 'not_found')
    ) {
      return;
    }

    let cancelled = false;

    const loadHistory = async () => {
      historyFetchMeta.current = { brandId, range, status: 'loading' };
      setHistoryLoading(true);
      setHistoryError('');
      try {
        const resp = await apiFetch(
          `/wallet/history?brandId=${encodeURIComponent(
            brandId
          )}&range=${encodeURIComponent(range)}`
        );
        const rows = Array.isArray(resp?.items)
          ? resp.items
          : Array.isArray(resp)
            ? resp
            : [];
        const normalized = rows.map((row) => {
          const rawEventType = String(row.eventType || '').toLowerCase();
          const knownEventTypes = new Set([
            'top_up',
            'contribution_debit',
            'fee_debit',
            'refund_contribution_credit',
            'manual_adjustment',
            'reversal',
          ]);
          const eventType = knownEventTypes.has(rawEventType)
            ? rawEventType
            : 'manual_adjustment';
          const nameKey = (row.businessName || '').toLowerCase().trim();
          const derivedKey =
            row.businessKey ||
            businessMapByName.get(nameKey) ||
            (eventType === 'top_up' ? 'ACCOUNT' : '');

          return {
            ...row,
            eventType,
            businessKey: derivedKey,
            referenceId:
              typeof row.referenceId === 'string' ? row.referenceId : '',
            grossSpendCents:
              typeof row.grossSpendCents === 'number'
                ? row.grossSpendCents
                : null,
            eligibleSpendCents:
              typeof row.eligibleSpendCents === 'number'
                ? row.eligibleSpendCents
                : null,
            contributionPercent:
              typeof row.contributionPercent === 'number'
                ? row.contributionPercent
                : null,
            contributionCents:
              typeof row.contributionCents === 'number'
                ? row.contributionCents
                : null,
            feeCents:
              typeof row.feeCents === 'number' ? row.feeCents : null,
            topUpCents:
              typeof row.topUpCents === 'number' ? row.topUpCents : null,
            balanceAfterCents:
              typeof row.balanceAfterCents === 'number'
                ? row.balanceAfterCents
                : null,
            status:
              typeof row.status === 'string' && row.status.trim()
                ? row.status.trim().toLowerCase()
                : null,
          };
        });

        if (!cancelled) {
          historyFetchMeta.current = { brandId, range, status: 'success' };
          setHistoryRows(normalized);
        }
      } catch (err) {
        console.error('Failed to load wallet history', err);
        if (!cancelled) {
          const status = err?.status === 404 ? 'not_found' : 'error';
          historyFetchMeta.current = { brandId, range, status };
          setHistoryRows([]);
          setHistoryError(
            err?.status === 404
              ? 'Wallet history is not available for this brand yet.'
              : 'Unable to load wallet history right now.'
          );
        }
      } finally {
        if (!cancelled) {
          setHistoryLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [
    showWalletHistory,
    brandId,
    range,
    businessMapByName,
    historyRows,
  ]);

  useEffect(() => {
    if (!showWalletHistory) return;
    if (historyLoading || historyRows.length > 0) return;
    if (!wallet?.recentDeductions || wallet.recentDeductions.length === 0) return;
    const meta = historyFetchMeta.current;
    if (!meta || (meta.status !== 'not_found' && meta.status !== 'error')) return;

    const mapped = wallet.recentDeductions.map((row) => {
      const spendCents = typeof row.transactionAmountCents === 'number'
        ? row.transactionAmountCents
        : null;
      const contributionCents = typeof row.contributionCents === 'number'
        ? row.contributionCents
        : null;
      const contributionPct =
        spendCents && contributionCents != null && spendCents > 0
          ? contributionCents / spendCents
          : null;
      return {
        eventType: 'contribution_debit',
        businessName: row.brandName || '',
        businessKey: row.brandId || '',
        referenceId: String(row.plaidTransactionId || row.id || '').trim(),
        grossSpendCents: spendCents,
        eligibleSpendCents: spendCents,
        contributionPercent:
          contributionPct != null ? contributionPct * 100 : null,
        contributionCents,
        feeCents: typeof row.feeCents === 'number' ? row.feeCents : null,
        topUpCents: null,
        balanceAfterCents: null,
        channel: null,
        locationLabel: null,
        status: 'debit',
        ts: row.effectiveDate,
      };
    });

    setHistoryError('');
    setHistoryRows(mapped);
  }, [showWalletHistory, historyLoading, historyRows.length, wallet?.recentDeductions]);

  useEffect(() => {
    if (!showWalletHistory || !historyRows.length) return;

    let needsUpdate = false;
      const mapped = historyRows.map((row) => {
        if (row.businessKey || !row.businessName) return row;
        const nameKey = (row.businessName || '').toLowerCase().trim();
        const derived =
          businessMapByName.get(nameKey) ||
        (row.eventType === 'top_up' ? 'ACCOUNT' : '');
        if (!derived) return row;
        needsUpdate = true;
        return { ...row, businessKey: derived };
    });

    if (needsUpdate) {
      setHistoryRows(mapped);
    }
  }, [showWalletHistory, historyRows, businessMapByName]);

  // walletCard data blob for <WalletAndPaymentsCard/>
  const walletCardData = !loading && wallet
    ? {
        asOfDisplay: walletTs
          ? new Date(walletTs).toLocaleString()
          : '—',
        mode: wallet?.paymentType === 'manual' ? 'manual' : 'auto',
        walletBalance: dollars(wallet?.balanceCents || 0),
        paymentTypeDisplay: paymentTypeText,
        lastTopUp: wallet?.lastTopupAt
          ? new Date(wallet.lastTopupAt).toLocaleString()
          : '—',
        minTopUp: effectiveTotals
          ? dollars(effectiveTotals.minTopUpCents || 0)
          : '—',
        replenishAmount: effectiveTotals
          ? dollars(effectiveTotals.reloadCents || 0)
          : '—',
        threshold: effectiveTotals
          ? dollars(thresholdDisplayCents || 0)
          : '—',
        fundedToDate: dollars(wallet?.fundedToDateCents || 0),
        spentOnContributions: dollars(wallet?.spentOnContributionsCents || 0),
        spentOnFees: dollars(wallet?.spentOnFeesCents || 0),
        onFundWallet: handleFundWallet,
        onViewWalletHistory: openWalletHistory,
      }
    : null;

  const showBrandCards = !!walletCardData;
  const brandCardsLoading = loading;
  const businessListLoading =
    loading && !businessesToShow.length;

  // perf card data
  const performanceCardData = useMemo(() => {
    return {
      asOfDisplay: perfTs
        ? new Date(perfTs).toLocaleString()
        : '—',
      contribLifetime: dollars(
        performance?.contributions?.lifetimeCents ?? 0
      ),
      contrib30d: dollars(
        performance?.contributions?.last30dCents ?? 0
      ),
      feesLifetime: dollars(performance?.fees?.lifetimeCents ?? 0),
      fees30d: dollars(performance?.fees?.last30dCents ?? 0),
      txLifetime: fmtInt(
        performance?.transactions?.lifetime ?? 0
      ),
      tx30d: fmtInt(
        performance?.transactions?.last30d ?? 0
      ),
      eligibleSalesLifetime: dollars(
        performance?.eligibleSales?.lifetimeCents ?? 0
      ),
      eligibleSales30d: dollars(
        performance?.eligibleSales?.last30dCents ?? 0
      ),
    };
  }, [performance, perfTs]);

  const filteredHistoryRows = useMemo(() => {
    return historyRows.filter((row) => {
      if (historyFilters.eventType !== 'ALL') {
        if (row.eventType !== historyFilters.eventType) return false;
      }

      if (historyFilters.businessId !== 'ALL') {
        if (!row.businessKey || row.businessKey !== historyFilters.businessId)
          return false;
      }

      return true;
    });
  }, [historyRows, historyFilters]);

  useEffect(() => {
    if (historyFilters.businessId === 'ALL') return;
    if (historyFilters.businessId === 'ACCOUNT') return;
    const exists = businessIdSet.has(historyFilters.businessId);
    if (!exists) {
      setHistoryFilters((prev) => ({ ...prev, businessId: 'ALL' }));
    }
  }, [businessIdSet, historyFilters.businessId]);

  // Accordion expand handlers
  const isRowOpen = (id) => !!expanded[id];
  const handleToggleRow = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  if (!accountId) {
    return (
      <div style={styles.fullscreen}>
        <div style={styles.membershipGuard}>
          <p>Missing account context. Please select or create an account.</p>
        </div>
      </div>
    );
  }

  if (!membershipLoading && !membership) {
    return (
      <div style={styles.fullscreen} className="bus-dash-shell">
        <div style={styles.membershipGuard}>
          <p>{membershipError || 'You do not have access to this account.'}</p>
        </div>
      </div>
    );
  }

  /* ---------- render ---------- */
  return (
    <div style={styles.fullscreen} className="bus-dash-shell">
      <style>{`.bus-dash-shell select {
        background: rgba(19,36,46,0.92);
        color: white;
      }
      .bus-dash-shell select option,
      .bus-dash-shell select optgroup {
        color: #123642;
        background: #f7fbfc;
      }
      @media (forced-colors: active) {
        .bus-dash-shell select {
          forced-color-adjust: auto;
        }
      }`}</style>
      <div style={styles.dashboardMain}>
        <div style={styles.dashboardInner}>
          <HeaderBar
            userName={ownerName}
            lastLoginDisplay={lastLoginDisplay}
            onAddBusiness={handleAddBusiness}
            onAddStore={handleOpenAddStore}
            canAddStore={!!brandId}
            addStoreHint={addStoreHint}
            onLogout={handleLogout}
            canAddBusiness={can('org_admin')}
          />

          <div style={styles.dashboardTopRow}>
            {showBrandCards ? (
              <div style={styles.brandCardsRow}>
                <WalletAndPaymentsCard
                  data={walletCardData}
                  onSetMode={requestToggleMode}
                  modeDisabled={isSavingMode}
                />
                <PerformanceCard data={performanceCardData} />
              </div>
            ) : brandCardsLoading ? (
              <FrostedCard style={styles.placeholderCard}>
                <div style={styles.placeholderTitle}>
                  Loading brand data…
                </div>
                <div style={styles.placeholderBody}>
                  Pulling account-level wallet and performance info.
                </div>
              </FrostedCard>
            ) : (
              <FrostedCard style={styles.placeholderCard}>
                <div style={styles.placeholderTitle}>
                  Select a business
                </div>
                <div style={styles.placeholderBody}>
                  Choose a brand to manage settings and store operations.
                </div>
              </FrostedCard>
            )}
          </div>

          {driftError ? (
            <div style={styles.inlineError}>{driftError}</div>
          ) : null}
          {walletFundBanner?.text ? (
            <div
              style={
                walletFundBanner.tone === 'success'
                  ? styles.walletFundBannerSuccess
                  : styles.walletFundBannerNeutral
              }
            >
              {walletFundBanner.text}
            </div>
          ) : null}
          {driftLoading ? (
            <div style={styles.inlineInfo}>Checking for location changes…</div>
          ) : null}

          <BusinessSection
            businesses={businessSectionData}
            currentBrandId={brandId}
            attention={driftCandidates}
            onToggleRow={handleToggleRow}
            isRowOpen={isRowOpen}
            loading={businessListLoading}
            onResolve={handleOpenSuggestionModal}
            onOpenContributionChange={openContributionModal}
          />
        </div>
      </div>

      {contributionModalOpen ? (
        <div style={styles.modalBackdrop} onClick={closeContributionModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeaderRow}>
              <div style={styles.modalTitle}>Change Base Giving Rate</div>
              <button onClick={closeContributionModal} style={styles.dismissBtn}>×</button>
            </div>
            <div style={styles.modalSubtitle}>{contributionModalCtx.displayName || 'Business'}</div>

            {contributionLoading ? <div style={styles.modalInfoText}>Loading settings…</div> : null}
            {contributionError ? <div style={styles.modalError}>{contributionError}</div> : null}

            {!contributionLoading ? (
              <div style={styles.modalContent}>
                <div style={styles.modalSection}>
                  <div style={styles.modalFieldLabel}>Current rate</div>
                  <div>{`${Number(contributionConfig?.currentPercent || 0)}%`}</div>
                </div>
                <div style={styles.modalSection}>
                  <div style={styles.modalFieldLabel}>Next eligible effective date</div>
                  <div>{contributionConfig?.nextEligibleAt ? new Date(contributionConfig.nextEligibleAt).toLocaleDateString() : '—'}</div>
                </div>
                <div style={styles.modalSection}>
                  <div style={styles.modalFieldLabel}>Scheduled change</div>
                  <div>
                    {contributionConfig?.pending
                      ? `${Number(contributionConfig.pending.toPercent)}% effective ${new Date(contributionConfig.pending.effectiveAt).toLocaleDateString()}`
                      : 'None'}
                  </div>
                </div>

                <label style={styles.modalLabel}>New contribution rate</label>
                <select
                  value={contributionForm.toPercent}
                  onChange={(e) => setContributionForm((prev) => ({ ...prev, toPercent: e.target.value }))}
                  style={styles.modalSelect}
                >
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={String(n)}>{n}%</option>
                  ))}
                </select>

                <label style={styles.modalLabel}>Effective date</label>
                <input
                  type="date"
                  value={contributionForm.effectiveDate}
                  min={toDateInput(contributionConfig?.nextEligibleAt)}
                  onChange={(e) => setContributionForm((prev) => ({ ...prev, effectiveDate: e.target.value }))}
                  style={styles.modalInput}
                />

                <div style={styles.modalInfoText}>
                  This change applies only to future eligible transactions. Past transactions will not be recalculated.
                </div>

                <div style={styles.modalActionsRow}>
                  {contributionConfig?.pending ? (
                    <button
                      type="button"
                      style={styles.secondaryBtn}
                      onClick={cancelContributionChange}
                      disabled={contributionSaving}
                    >
                      Cancel scheduled change
                    </button>
                  ) : null}
                  <button
                    type="button"
                    style={styles.primaryBtn}
                    onClick={submitContributionChange}
                    disabled={contributionSaving}
                  >
                    {contributionSaving ? 'Saving…' : (contributionConfig?.pending ? 'Update scheduled change' : 'Schedule change')}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Wallet History Modal */}
      {showWalletHistory && (
        <div
          style={styles.modalBackdrop}
          onClick={closeWalletHistory}
        >
          <div
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 16,
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ display: 'grid', gap: 8 }}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>
                    Wallet History
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.8 }}>
                    A running record of contributions, fees, and wallet funding.
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <select
                    value={historyFilters.range}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value !== historyFilters.range) {
                        historyFetchMeta.current = { brandId: null, range: null, status: 'idle' };
                        setHistoryRows([]);
                        setHistoryError('');
                      }
                      setHistoryFilters((prev) => ({ ...prev, range: value }));
                    }}
                    style={filterSelectStyle}
                  >
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="all">All time</option>
                  </select>

                  <select
                    value={historyFilters.businessId}
                    onChange={(e) => {
                      setHistoryFilters((prev) => ({
                        ...prev,
                        businessId: e.target.value,
                      }));
                      setHistoryError('');
                    }}
                    style={filterSelectStyle}
                  >
                    <option value="ALL">All Businesses</option>
                    {businessOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.displayName}
                      </option>
                    ))}
                    <option value="ACCOUNT">Account Wallet</option>
                  </select>

                  <select
                    value={historyFilters.eventType}
                    onChange={(e) => {
                      setHistoryFilters((prev) => ({
                        ...prev,
                        eventType: e.target.value,
                      }));
                      setHistoryError('');
                    }}
                    style={filterSelectStyle}
                  >
                    <option value="ALL">All Events</option>
                    <option value="top_up">Top-Up</option>
                    <option value="contribution_debit">Contribution Debit</option>
                    <option value="fee_debit">Fee Debit</option>
                    <option value="refund_contribution_credit">Refund Contribution Credit</option>
                    <option value="manual_adjustment">Manual Adjustment</option>
                    <option value="reversal">Reversal</option>
                  </select>

                  <PeachButton
                    onClick={handleExportWalletHistory}
                    disabled={!brandId || historyExporting}
                    style={{
                      whiteSpace: 'nowrap',
                      opacity: brandId && !historyExporting ? 1 : 0.6,
                      pointerEvents: brandId && !historyExporting ? 'auto' : 'none',
                    }}
                  >
                    {historyExporting ? 'Exporting...' : 'Export CSV'}
                  </PeachButton>

                  <button
                    onClick={closeWalletHistory}
                    style={styles.dismissBtn}
                  >
                    ×
                  </button>
                </div>
              </div>

              {historyError && (
                <div
                  style={{
                    background: 'rgba(239,68,68,0.16)',
                    border: '1px solid rgba(239,68,68,0.35)',
                    color: '#FECACA',
                    padding: '8px 12px',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                >
                  {historyError}
                </div>
              )}

              <div
                style={{
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 12,
                  overflow: 'hidden',
                  maxHeight: '50vh',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    overflowX: 'auto',
                    overflowY: 'auto',
                    maxHeight: '50vh',
                  }}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      minWidth: 800,
                      fontSize: 13,
                      color: 'white',
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={thStyle}>Date / Time</th>
                        <th style={thStyle}>Business / Location</th>
                        <th style={thStyle}>Event Type</th>
                        <th style={thStyle}>Reference ID</th>
                        <th style={thStyle}>Gross Spend</th>
                        <th style={thStyle}>Eligible Spend</th>
                        <th style={thStyle}>Contribution %</th>
                        <th style={thStyle}>Contribution</th>
                        <th style={thStyle}>Fee</th>
                        <th style={thStyle}>Balance After</th>
                        <th style={thStyle}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyLoading ? (
                        <tr>
                          <td style={tdEmptyStyle} colSpan={11}>
                            Loading history…
                          </td>
                        </tr>
                      ) : historyError ? (
                        <tr>
                          <td style={tdEmptyStyle} colSpan={11}>
                            {historyError}
                          </td>
                        </tr>
                      ) : filteredHistoryRows.length === 0 ? (
                        <tr>
                          <td style={tdEmptyStyle} colSpan={11}>
                            No wallet activity for this view.
                          </td>
                        </tr>
                      ) : (
                        filteredHistoryRows.map((row, idx) => {
                          const isTopUp = row.eventType === 'top_up';
                          const tsDisplay = row.ts
                            ? new Date(row.ts).toLocaleString()
                            : '—';
                          const eventLabelMap = {
                            top_up: 'Top-Up',
                            contribution_debit: 'Contribution Debit',
                            fee_debit: 'Fee Debit',
                            refund_contribution_credit: 'Refund Contribution Credit',
                            manual_adjustment: 'Manual Adjustment',
                            reversal: 'Reversal',
                          };

                          const businessDisplay = isTopUp
                            ? 'Account Wallet'
                            : `${row.businessName || '—'}${
                                row.locationLabel ? ` — ${row.locationLabel}` : ''
                              }`;

                          const topUpAmountDisplay =
                            row.topUpCents != null
                              ? `+${centsToDollar(row.topUpCents)}`
                              : null;

                          const eventDisplay = isTopUp
                            ? `Top-Up (${[
                                row.topUpMethod || 'Funding',
                                topUpAmountDisplay,
                              ]
                                .filter(Boolean)
                                .join(' • ')})`
                            : eventLabelMap[row.eventType] || 'Event';

                          const referenceDisplay = row.referenceId || '—';

                          const grossSpendDisplay = isTopUp
                            ? '—'
                            : centsToDollar(row.grossSpendCents);

                          const eligibleSpendDisplay = isTopUp
                            ? '—'
                            : centsToDollar(row.eligibleSpendCents);

                          const pctDisplay = isTopUp
                            ? '—'
                            : row.contributionPercent != null
                              ? `${Number(row.contributionPercent || 0).toFixed(2)}%`
                              : '—';

                          const contribDisplay = isTopUp
                            ? '—'
                            : centsToDollar(row.contributionCents);

                          const feeDisplay = isTopUp
                            ? '—'
                            : centsToDollar(row.feeCents);

                          const balDisplay = centsToDollar(
                            row.balanceAfterCents
                          );
                          const statusValue =
                            typeof row.status === 'string' && row.status
                              ? row.status.toLowerCase()
                              : (isTopUp ? 'credit' : '');
                          const isCredit = statusValue === 'credit';
                          const isDebit = statusValue === 'debit';
                          const directionColor = isCredit
                            ? '#22C55E'
                            : isDebit
                              ? '#F87171'
                              : 'rgba(255,255,255,0.9)';
                          const statusDisplay =
                            statusValue
                              ? statusValue.toUpperCase()
                              : '—';

                          return (
                            <tr
                              key={`${row.ts || idx}-${idx}`}
                              style={idx % 2 ? trAltStyle : undefined}
                            >
                              <td style={tdStyle}>{tsDisplay}</td>
                              <td style={tdStyle}>{businessDisplay}</td>
                              <td
                                style={{
                                  ...tdStyle,
                                  color: directionColor,
                                  fontWeight: 600,
                                }}
                              >
                                {eventDisplay}
                              </td>
                              <td style={tdStyle}>{referenceDisplay}</td>
                              <td style={tdNumStyle}>{grossSpendDisplay}</td>
                              <td style={tdNumStyle}>{eligibleSpendDisplay}</td>
                              <td style={tdNumStyle}>{pctDisplay}</td>
                              <td
                                style={{
                                  ...tdNumStyle,
                                  color:
                                    contribDisplay === '—'
                                      ? tdNumStyle.color
                                      : directionColor,
                                  fontWeight:
                                    contribDisplay === '—' ? tdNumStyle.fontWeight : 600,
                                }}
                              >
                                {contribDisplay}
                              </td>
                              <td
                                style={{
                                  ...tdNumStyle,
                                  color:
                                    feeDisplay === '—'
                                      ? tdNumStyle.color
                                      : directionColor,
                                  fontWeight:
                                    feeDisplay === '—' ? tdNumStyle.fontWeight : 600,
                                }}
                              >
                                {feeDisplay}
                              </td>
                              <td style={tdNumStyle}>{balDisplay}</td>
                              <td style={tdNumStyle}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    minWidth: 74,
                                    textAlign: 'center',
                                    padding: '2px 8px',
                                    borderRadius: 999,
                                    border: `1px solid ${isCredit ? 'rgba(34,197,94,0.6)' : isDebit ? 'rgba(248,113,113,0.6)' : 'rgba(255,255,255,0.24)'}`,
                                    background: isCredit
                                      ? 'rgba(34,197,94,0.16)'
                                      : isDebit
                                        ? 'rgba(248,113,113,0.16)'
                                        : 'rgba(255,255,255,0.08)',
                                    color: directionColor,
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                  }}
                                >
                                  {statusDisplay}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {walletFundMonitor.open && (
        <div
          style={styles.modalBackdrop}
          onClick={() => {
            if (walletFundMonitor.timedOut || walletFundMonitor.status === 'failed') {
              setWalletFundMonitor((prev) => ({ ...prev, open: false }));
            }
          }}
        >
          <div
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.walletFundStatusTitle}>
              {walletFundMonitor.status === 'failed' ? 'Payment Failed' : 'Processing Payment'}
            </div>
            <div style={styles.walletFundStatusBody}>
              {walletFundMonitor.message}
            </div>
            <div style={styles.walletFundStatusHint}>
              {walletFundMonitor.sessionId
                ? `Session: ${walletFundMonitor.sessionId}`
                : 'Waiting for checkout session confirmation.'}
            </div>
            {(walletFundMonitor.timedOut || walletFundMonitor.status === 'failed') && (
              <div style={styles.modalActionsRow}>
                <OutlineButton
                  onClick={() => setWalletFundMonitor((prev) => ({ ...prev, open: false }))}
                >
                  Close
                </OutlineButton>
              </div>
            )}
          </div>
        </div>
      )}

      {driftModalOpen && selectedCandidate && (
        <div style={styles.modalBackdrop} onClick={handleCloseSuggestionModal}>
          <div
            style={styles.modalLarge}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeaderRow}>
              <div style={styles.modalTitle}>Resolve Suggested Location</div>
              <div style={styles.modalSubtitle}>
                Confirm how you want to handle this new location.
              </div>
            </div>

            <div style={styles.modalSection}>
              <div style={styles.modalFieldLabel}>Name</div>
              <div>{selectedCandidate.name || 'New location'}</div>
            </div>

            <div style={styles.modalSection}>
              <div style={styles.modalFieldLabel}>Address</div>
              <div>
                {selectedCandidate.formattedAddress
                  || selectedCandidate.address
                  || 'Address unavailable'}
              </div>
            </div>

            <div style={styles.modalSection}>
              <div style={styles.modalFieldLabel}>Source</div>
              <div>
                {(selectedCandidate.source || 'unknown').toUpperCase()}
                {selectedCandidate.marketKey
                  ? ` • Market ${selectedCandidate.marketKey}`
                  : ''}
              </div>
            </div>

            {driftActionError ? (
              <div style={styles.modalError}>{driftActionError}</div>
            ) : null}

            {excludeModalOpen ? (
              <form
                style={styles.modalFormStack}
                onSubmit={handleTempExcludeSubmit}
              >
                <label style={styles.modalLabel} htmlFor="exclude-reason">
                  Reason
                </label>
                <select
                  id="exclude-reason"
                  style={styles.modalSelect}
                  value={excludeForm.reason}
                  onChange={(e) =>
                    handleExcludeFieldChange('reason', e.target.value)
                  }
                  disabled={driftActionLoading}
                >
                  <option value="">Select a reason…</option>
                  {(reasonsLoading && !exclusionReasons.length
                    ? FALLBACK_EXCLUSION_REASONS
                    : exclusionReasons
                  ).map((reason) => (
                    <option key={reason.code} value={reason.code}>
                      {reason.label}
                    </option>
                  ))}
                </select>

                <label style={styles.modalLabel} htmlFor="exclude-date">
                  Exclude until
                </label>
                <input
                  id="exclude-date"
                  type="date"
                  style={styles.modalInput}
                  value={excludeForm.date}
                  onChange={(e) =>
                    handleExcludeFieldChange('date', e.target.value)
                  }
                  min={new Date(Date.now() + 24 * 60 * 60 * 1000)
                    .toISOString()
                    .slice(0, 10)}
                  disabled={driftActionLoading}
                />

                <label style={styles.modalLabel} htmlFor="exclude-note">
                  Notes (optional)
                </label>
                <textarea
                  id="exclude-note"
                  style={styles.modalTextarea}
                  value={excludeForm.note}
                  onChange={(e) =>
                    handleExcludeFieldChange('note', e.target.value)
                  }
                  disabled={driftActionLoading}
                  placeholder="Add any context for your team"
                />

                <div style={styles.modalActionsRow}>
                  <OutlineButton
                    type="button"
                    onClick={() => {
                      setExcludeModalOpen(false);
                      setDriftActionError('');
                    }}
                    disabled={driftActionLoading}
                  >
                    Back
                  </OutlineButton>
                  <PeachButton type="submit" disabled={driftActionLoading}>
                    {driftActionLoading ? 'Saving…' : 'Temp Exclude'}
                  </PeachButton>
                </div>
              </form>
            ) : (
              <div style={styles.modalActionsColumn}>
                <PeachButton
                  onClick={handleEnrollSuggestion}
                  disabled={driftActionLoading}
                >
                  {driftActionLoading ? 'Saving…' : 'Enroll Location'}
                </PeachButton>
                <OutlineButton
                  onClick={handleOpenTempExcludeModal}
                  disabled={driftActionLoading}
                >
                  Temp Exclude
                </OutlineButton>
                <OutlineButton
                  onClick={() => handleSnoozeSuggestion(30)}
                  disabled={driftActionLoading}
                >
                  Snooze 30 days
                </OutlineButton>
                <GhostButton
                  onClick={handleIgnoreSuggestion}
                  disabled={driftActionLoading}
                >
                  Not My Store
                </GhostButton>
              </div>
            )}
          </div>
        </div>
      )}


      {addStoreOpen && (
        <AddStoreModal
          brandId={brandId}
          onClose={handleCloseAddStore}
          refreshBrandData={refreshBrandData}
          ensureExclusionReasons={ensureExclusionReasons}
          exclusionReasons={exclusionReasons}
          reasonsLoading={reasonsLoading}
          fallbackReasons={FALLBACK_EXCLUSION_REASONS}
          einOptions={addStoreEinOptions}
        />
      )}

      {/* Confirm payment mode modal */}
      {confirmOpen && (
        <div
          style={styles.modalBackdrop}
          onClick={() => setConfirmOpen(false)}
        >
          <div
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                marginBottom: 12,
              }}
            >
              Confirm payment mode change
            </div>
            <div
              style={{
                opacity: 0.85,
                marginBottom: 16,
              }}
            >
              Switch payment mode to{' '}
              <b>
                {confirmNextMode === 'manual'
                  ? 'Manual (1.75%)'
                  : 'Auto (1.00%)'}
              </b>
              ?
            </div>
            <div
              style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              <OutlineButton
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </OutlineButton>
              <PeachButton
                onClick={() =>
                  handleToggleMode(confirmNextMode)
                }
                disabled={isSavingMode}
              >
                {isSavingMode ? 'Updating…' : 'Confirm'}
              </PeachButton>
            </div>
          </div>
        </div>
      )}

      {/* bottom-left corner rypil mark */}
      <img
        src={rypilLo}
        alt="rypil mark"
        style={styles.badge}
      />
    </div>
  );
}

/* ---------- styles ---------- */
const filterSelectStyle = {
  background: 'rgba(255,255,255,0.08)',
  color: 'white',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 14,
  minWidth: 140,
  cursor: 'pointer',
};

const thStyle = {
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 12,
  color: 'rgba(255,255,255,0.9)',
  padding: '10px 12px',
  borderBottom: '1px solid rgba(255,255,255,0.2)',
  whiteSpace: 'nowrap',
  background: 'rgba(11,18,24,0.95)',
  position: 'sticky',
  top: 0,
  zIndex: 3,
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid rgba(255,255,255,0.07)',
  verticalAlign: 'top',
  lineHeight: 1.4,
};

const tdNumStyle = {
  ...tdStyle,
  textAlign: 'right',
  fontFeatureSettings: '"tnum"',
};

const tdEmptyStyle = {
  ...tdStyle,
  textAlign: 'center',
  opacity: 0.7,
};

const trAltStyle = {
  background: 'rgba(255,255,255,0.03)',
};

const styles = {
  fullscreen: {
    backgroundImage: `url(${tealBack})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: '100vh',
    width: '100%',
    color: 'white',
    fontFamily: 'Helvetica, Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  membershipGuard: {
    margin: '0 auto',
    paddingTop: 160,
    maxWidth: 720,
    textAlign: 'center',
    fontSize: 20,
    lineHeight: 1.5,
    color: 'white',
  },

  headerRow: {
    margin: '32px 0 24px',
  },
  headerCard: {
    display: 'flex',
    gap: 24,
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  headerLeftGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    minWidth: 240,
  },
  headerLogoWrap: { display: 'flex', alignItems: 'center' },
  headerLogo: { height: 72 },
  headerIdentity: {
    display: 'grid',
    gap: 4,
    textAlign: 'left',
    alignItems: 'flex-start',
  },
  headerUser: { fontSize: 18, fontWeight: 700 },
  headerContext: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 6,
    fontSize: 12,
    opacity: 0.8,
  },
  headerContextLabel: { fontWeight: 500 },
  headerContextValue: { fontWeight: 600 },
  headerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    flexWrap: 'wrap',
  },
  headerStoreAction: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
  },
  headerStoreHint: {
    fontSize: 12,
    opacity: 0.72,
  },

  dashboardMain: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 0 80px',
  },
  dashboardInner: {
    width: 'min(1280px, 100%)',
    padding: '0 24px',
    display: 'grid',
    gap: 24,
  },

  dashboardTopRow: {
    display: 'grid',
    gap: 24,
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
  },
  brandCardsRow: {
    display: 'grid',
    gap: 24,
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  },

  walletCard: { display: 'grid', gap: 24 },

  cardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  cardTitle: { fontSize: 18, fontWeight: 700 },
  cardMeta: { fontSize: 12, opacity: 0.8 },
  modeToggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },

  modeToggleWrap: {
    display: 'flex',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 999,
    overflow: 'hidden',
  },
  modeToggleBtn: {
    padding: '6px 14px',
    fontWeight: 600,
    background: 'transparent',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
  },
  modeToggleActive: {
    background: PEACH,
    color: '#1A1A1A',
  },
  modeToggleButtonActive: {
    background: 'rgba(235,164,119,0.18)',
    border: `1px solid ${PEACH}`,
    color: PEACH,
  },

  walletGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
  },
  walletActions: { display: 'flex', gap: 12, flexWrap: 'wrap' },

  statCell: {
    padding: 12,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
  },
  statLabel: { fontSize: 12, opacity: 0.82, marginBottom: 4 },
  statValue: { fontSize: 18, fontWeight: 700 },

  perfCard: { display: 'grid', gap: 20 },
  perfGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
  },

  tipBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    background: 'rgba(235,164,119,0.18)',
    border: `1px solid ${PEACH}`,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color: PEACH,
  },
  tipBadgeIcon: { width: 14, height: 14 },
  newLocationBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 8px',
    borderRadius: 999,
    background: 'rgba(248,113,113,0.22)',
    border: '1px solid rgba(248,113,113,0.45)',
    color: '#F87171',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },

  brandAvatarOuter: {
    width: 44,
    height: 44,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.18)',
    overflow: 'hidden',
    background: 'rgba(255,255,255,0.06)',
    display: 'grid',
    placeItems: 'center',
  },
  brandAvatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  brandAvatarFallback: { fontSize: 16, fontWeight: 700 },

  businessSectionCard: { display: 'grid', gap: 20 },
  businessList: { display: 'grid', gap: 16 },

  bizRow: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.05)',
    overflow: 'hidden',
    transition: 'border-color 150ms ease, background 150ms ease',
  },
  bizRowOpen: {
    borderColor: PEACH,
    background: 'rgba(235,164,119,0.12)',
  },
  bizRowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 18px',
    gap: 16,
    cursor: 'pointer',
  },
  bizRowLeft: {
    display: 'flex',
    gap: 14,
    alignItems: 'center',
  },
  bizRowText: { display: 'grid', gap: 6 },

  bizRowLine1: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 700,
    fontSize: 16,
  },
  bizRowName: { fontSize: 16, fontWeight: 700 },

  bizRowLine2: { fontSize: 12, opacity: 0.8 },

  bizRowLine3: {
    fontSize: 12,
    opacity: 0.82,
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },

  bizRowLine4: { fontSize: 12, opacity: 0.8 },
  bizRowLine5: { fontSize: 12, opacity: 0.78 },
  bizRowLine6: { fontSize: 12, opacity: 0.75 },

  bizRowAttention: {
    borderColor: 'rgba(248,113,113,0.6)',
    boxShadow: '0 0 0 1px rgba(248,113,113,0.35)',
    background: 'rgba(248,113,113,0.08)',
  },
  bizRowRight: { fontSize: 18, opacity: 0.8 },
  chevron: { fontSize: 18, opacity: 0.75 },

  bizRowBody: {
    padding: '0 18px 18px',
    display: 'grid',
    gap: 12,
  },

  attentionPill: {
    background: 'rgba(248,113,113,0.2)',
    color: '#F87171',
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
  },
  attentionPanel: {
    display: 'grid',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    border: '1px solid rgba(248,113,113,0.36)',
    background: 'rgba(248,113,113,0.12)',
  },
  attentionPanelHeader: {
    fontSize: 13,
    fontWeight: 700,
    color: '#FCA5A5',
  },
  attentionRow: {
    display: 'flex',
    gap: 16,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    padding: '12px 14px',
    borderRadius: 10,
    background: 'rgba(11,18,24,0.45)',
    border: '1px solid rgba(248,113,113,0.24)',
  },
  attentionRowMain: {
    display: 'grid',
    gap: 6,
    flex: '1 1 220px',
    minWidth: 200,
  },
  attentionRowName: { fontWeight: 700, color: '#FCA5A5' },
  attentionRowMeta: { fontSize: 12, opacity: 0.8 },
  attentionRowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  resolveButton: {
    background: '#F87171',
    color: '#0B1218',
    border: 'none',
    borderRadius: 999,
    padding: '6px 14px',
    fontWeight: 700,
    cursor: 'pointer',
  },

  locationRow: {
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.04)',
    display: 'grid',
    gap: 6,
  },
  locationMain: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
  },
  locationName: { fontWeight: 600 },
  locationStatus: {
    padding: '4px 8px',
    borderRadius: 999,
    fontSize: 12,
  },
  status_active: {
    background: 'rgba(16,185,129,0.18)',
    color: '#10B981',
  },
  status_temp_excluded: {
    background: 'rgba(239,68,68,0.18)',
    color: '#EF4444',
  },
  locationNote: { fontSize: 12, opacity: 0.75 },

  emptyState: {
    opacity: 0.75,
    padding: '24px 0',
    textAlign: 'center',
  },

  inlineError: {
    background: 'rgba(248,113,113,0.16)',
    border: '1px solid rgba(248,113,113,0.35)',
    color: '#FCA5A5',
    padding: '10px 14px',
    borderRadius: 12,
    fontSize: 13,
  },
  inlineInfo: {
    fontSize: 13,
    opacity: 0.75,
    padding: '6px 0 12px',
  },
  walletFundBannerSuccess: {
    background: 'rgba(16,185,129,0.18)',
    border: '1px solid rgba(16,185,129,0.45)',
    color: '#6EE7B7',
    borderRadius: 10,
    padding: '10px 12px',
    marginBottom: 12,
    fontSize: 13,
    fontWeight: 600,
  },
  walletFundBannerNeutral: {
    background: 'rgba(148,163,184,0.18)',
    border: '1px solid rgba(148,163,184,0.35)',
    color: '#E2E8F0',
    borderRadius: 10,
    padding: '10px 12px',
    marginBottom: 12,
    fontSize: 13,
    fontWeight: 600,
  },
  walletFundStatusTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 10,
  },
  walletFundStatusBody: {
    fontSize: 14,
    lineHeight: 1.5,
    opacity: 0.9,
    marginBottom: 10,
  },
  walletFundStatusHint: {
    fontSize: 12,
    opacity: 0.65,
    wordBreak: 'break-all',
    marginBottom: 14,
  },

  placeholderCard: {
    display: 'grid',
    gap: 8,
    alignContent: 'center',
    minHeight: 220,
  },
  placeholderTitle: { fontSize: 16, fontWeight: 600 },
  placeholderBody: { fontSize: 13, opacity: 0.8 },

  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'grid',
    placeItems: 'center',
    zIndex: 50,
    padding: 16,
  },
  modal: {
    width: 'min(100%, 900px)',
    maxHeight: '80vh',
    overflow: 'auto',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(11,18,24,0.94)',
    padding: 24,
    display: 'grid',
    gap: 16,
  },
  modalLarge: {
    width: 'min(100%, 640px)',
    maxHeight: '80vh',
    overflow: 'auto',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(11,18,24,0.94)',
    padding: 24,
    display: 'grid',
    gap: 16,
  },
  modalContent: {
    display: 'grid',
    gap: 16,
  },
  modalHeaderRow: {
    display: 'grid',
    gap: 4,
  },
  modalTitle: { fontSize: 18, fontWeight: 700 },
  modalSubtitle: { fontSize: 13, opacity: 0.72 },
  modalLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    opacity: 0.72,
  },
  modalInput: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.05)',
    color: 'white',
    boxSizing: 'border-box',
  },
  modalTextarea: {
    width: '100%',
    minHeight: 80,
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.05)',
    color: 'white',
    resize: 'vertical',
  },
  modalSelect: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.05)',
    color: 'white',
  },
  modalCheckboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    opacity: 0.85,
  },
  modalActionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 8,
  },
  modalActionsColumn: {
    display: 'grid',
    gap: 12,
  },
  modalFieldRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  discoverActionsRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  modalInfoText: {
    fontSize: 13,
    opacity: 0.72,
  },
  inlineLinkButton: {
    marginLeft: 8,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#7dd3fc',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  primaryBtn: {
    border: 'none',
    borderRadius: 10,
    padding: '10px 14px',
    background: 'linear-gradient(120deg,#f59e0b,#fb7185)',
    color: '#041018',
    fontWeight: 700,
    cursor: 'pointer',
  },
  secondaryBtn: {
    border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: 10,
    padding: '10px 14px',
    background: 'transparent',
    color: 'white',
    fontWeight: 600,
    cursor: 'pointer',
  },
  discoverList: {
    display: 'grid',
    gap: 12,
  },
  discoverRow: {
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    background: 'rgba(255,255,255,0.05)',
    padding: '14px 16px',
    display: 'grid',
    gap: 10,
  },
  discoverRowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  discoverRowIdentity: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flex: '1 1 auto',
  },
  discoverRowIdentityText: {
    display: 'grid',
    gap: 4,
  },
  discoverRowNameLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  discoverRowName: {
    fontSize: 16,
    fontWeight: 700,
  },
  discoverSourceBadge: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.24)',
    padding: '2px 8px',
    opacity: 0.85,
  },
  discoverRowIdentitySub: {
    fontSize: 12,
    opacity: 0.72,
  },
  discoverIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
  },
  discoverIconImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  discoverIconFallback: {
    fontSize: 20,
  },
  discoverRowAddress: {
    fontSize: 13,
    opacity: 0.85,
  },
  discoverTagRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: -2,
  },
  discoverTag: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    padding: '4px 8px',
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.08)',
    opacity: 0.85,
  },
  discoverStatusTag: {
    borderColor: 'rgba(16,185,129,0.4)',
    background: 'rgba(16,185,129,0.18)',
    color: '#6EE7B7',
    fontWeight: 600,
  },
  discoverInfoGrid: {
    display: 'grid',
    gap: 6,
    fontSize: 12,
    opacity: 0.85,
  },
  discoverInfoItem: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'baseline',
  },
  discoverInfoLabel: {
    fontWeight: 600,
    minWidth: 100,
  },
  discoverInfoValue: {
    opacity: 0.85,
  },
  discoverInfoLink: {
    color: '#93C5FD',
    textDecoration: 'none',
  },
  discoverHoursBox: {
    marginTop: 6,
    padding: '10px 12px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    fontSize: 12,
    lineHeight: 1.45,
    whiteSpace: 'pre-line',
  },
  discoverActions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 12,
  },
  tipAutoBadgeBase: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  tipAutoBadgeLikely: {
    background: 'rgba(16,185,129,0.18)',
    border: '1px solid rgba(16,185,129,0.45)',
    color: '#6EE7B7',
  },
  tipAutoBadgeUnlikely: {
    background: 'rgba(148,163,184,0.18)',
    border: '1px solid rgba(148,163,184,0.35)',
    color: '#E2E8F0',
  },
  modalError: {
    background: 'rgba(248,113,113,0.16)',
    border: '1px solid rgba(248,113,113,0.35)',
    color: '#FCA5A5',
    padding: '8px 12px',
    borderRadius: 10,
    fontSize: 12,
  },
  modalSection: {
    display: 'grid',
    gap: 4,
    fontSize: 13,
  },
  modalFieldLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    opacity: 0.6,
  },
  modalFormStack: {
    display: 'grid',
    gap: 16,
  },
  dismissBtn: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: 'white',
    fontSize: 24,
    cursor: 'pointer',
  },

  badge: {
    position: 'fixed',
    left: 16,
    bottom: 16,
    width: 40,
    height: 40,
    opacity: 0.9,
  },
};
